import { useEffect, useRef, useState, useMemo, useContext } from "react";

import { useNavigate, useLocation } from "react-router-dom";
import { API, graphqlOperation } from "aws-amplify";
import { validateVoucher, getReservationInfo } from "../graphql/queries";
import { authPayment, getCompany } from "../graphql/client-queries";
import {
  upsertReservationCustomer,
  setReservationInfo,
  setReservationPaymentStatus,
  cancelReservationCustomer,
} from "../graphql/mutations";
import { calculateDiscountedPrice } from "../shared/pricing-utilities";
import {
  captureError,
  getFixedLanguage,
  getFixedLanguageSet,
  gtagPageView,
} from "../utilities";
import { centsToLocalString } from "../shared/money";
import { useFormik } from "formik";
import {
  askSsn,
  askDriverSsn,
  disableCreditcheck,
  paymentEnabled,
  strongAuthEnabled,
  showPortInstruction,
  vismaPayMaxPayment,
  enableMinimalCustomerInput,
  enablePrivacyPolicy,
  paymentOnPickupLabel,
  differentDriverLabel,
  disableAdditionalDriver,
  disableCustomerIsCompany,
  hideVouchers,
} from "../features";
import * as Sentry from "@sentry/react";
import { AppContext } from "../contextProviders/AppContext";
import PaymentMethods from "../components/PaymentMethods";
import OrderSummary from "../components/OrderSummary";
import AlreadyReservedDialog from "../components/AlreadyReservedDialog";
import {
  Alert,
  Grid,
  FormControlLabel,
  Checkbox,
  TextField,
  Typography,
  Container,
  FormHelperText,
  FormControl,
  Card,
  CardContent,
  useTheme,
  Link,
  RadioGroup,
  Radio,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  ClickAwayListener,
  Snackbar,
  InputAdornment,
  Button,
  Stack,
} from "@mui/material";

import CircularProgress from "@mui/material/CircularProgress";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ContractTemplate from "../shared/print-contracts/ContractTemplate";
import ContractTemplateEbikeRental from "../shared/print-contracts/ContractTemplateEbikeRental";
import ContractTemplateJetRental from "../shared/print-contracts/ContractTemplateJetRental";
import InfoIcon from "@mui/icons-material/Info";
import { Close } from "@mui/icons-material";

import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DoneIcon from "@mui/icons-material/Done";
import CreateIcon from "@mui/icons-material/Create";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import PaymentIcon from "@mui/icons-material/Payment";
import ButtonComponent from "../components/ButtonComponent";
import { translation } from "../components/Dictionary";
import { Box } from "@mui/system";

import PhoneNumberField from "../components/PhoneNumberField";
import {
  parsePhoneNumberFromString,
  isValidPhoneNumber,
  parsePhoneNumberWithError,
  ParseError,
} from "libphonenumber-js";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { gtmAddShippingInfo } from "../gtm";
import { ContractTemplatePreview } from "../components/ContractTemplate/contract-template-preview";

// Also used at PaymentMethods
const PaymentModeEnum = Object.freeze({
  now: "PENDING",
  now_deposit: "PENDING_DEPOSIT",
  onPickup: "",
});

const submitVariantEnum = Object.freeze({
  upsertOnly: 1, // Only Upsert
  upsertAndPay: 2, // Upsert & Visma Pay
  upsertAndAuth: 3, // Upsert & Telia Id
  payOnly: 4, // Only Visma Pay
  authOnly: 5, // Only Telia Id
  updateForPickup: 6, // Update and finalize, no Pay or Telia.
});

const INITIAL_FORM_DATA = {
  firstName: "",
  lastName: "",
  address: "",
  postalCode: "",
  city: "",
  phone: "",
  phoneCountryCode: "",
  email: "",
  emailRepeat: "",
  ssn: "",
  externalNotes: "",
  acceptTerms: false,
  acceptPrivacyPolicy: false,
  acceptPortTerms: false,
  acceptCreditCheck: false,
  differentDriver: false,
  driverName: "",
  driverSSN: "",
  driverAddress: "",
  driverPostalCode: "",
  driverCity: "",
  driverPhone: "",
  driverPhoneCountryCode: "",
  driverEmail: "",
  additionalDriver: false,
  additionalDriverName: "",
  additionalDriverPhone: "",
  additionalDriverPhoneCountryCode: "",
  isCompany: false,
  companyName: "",
  companyBusinessId: "",
  companyBillingRef: "",
  companyBillingAddress: "",
  paymentMethod: "",
  paymentMode: PaymentModeEnum.onPickup,
  submitVariant: null,
  bypassAuthentication: false,
};

//// Prefill fields in development
// if (process.env.REACT_APP_ENV === "local") {
//   INITIAL_FORM_DATA.name = "Erkki Esimerkki";
//   INITIAL_FORM_DATA.address = "Testikatu 123";
//   INITIAL_FORM_DATA.postalCode = "53850";
//   INITIAL_FORM_DATA.city = "Lappeenranta";
//   INITIAL_FORM_DATA.phone = "04412345123";
// }

const doUpsertReservation = async (
  reservation,
  paymentMode,
  bypassAuthentication
) => {
  const response = await API.graphql(
    graphqlOperation(upsertReservationCustomer, {
      reservation: reservation,
      paymentMode: paymentMode,
      bypassAuthentication: bypassAuthentication,
    })
  );

  return response.data.upsertReservationCustomer;
};

// For now the first name and last name are saved in one variable "name" to db
const getName = (values) => {
  const name = `${values.firstName} ${values.lastName}`;
  return name;
};

const getPhone = (values, phoneFieldName) => {
  let phoneNumber, isEnabled;
  const countryCode = values[phoneFieldName + "CountryCode"];
  const phone = values[phoneFieldName];
  if (phoneFieldName === "phone") {
    isEnabled = true;
  }
  if (phoneFieldName === "driverPhone") {
    isEnabled = values.differentDriver;
  }
  if (phoneFieldName === "additionalDriverPhone") {
    isEnabled = values.additionalDriver;
  }
  if (!isEnabled) return;
  try {
    phoneNumber = parsePhoneNumberFromString(countryCode + phone).number;
  } catch (e) {
    phoneNumber = phone ?? "";
    console.error(
      "Phone number parse failure",
      { countryCode: countryCode },
      { phone: phone },
      { defaultToPhone: phoneNumber }
    );
    captureError("Failed to parse phoneNumber", "PARSE_PHONE_FAILED", e);
  }
  return phoneNumber;
};

// List alphabetically sorted services filtered by categories

// Show basic info of selected reservation offer

export default function Reservation() {
  const handleChange = (panel) => (event, isExpanded) => {
    setExpandedReservationStep(isExpanded ? panel : false);
  };

  const [disableAuthenticationStep, setDisableAuthenticationStep] =
    useState(true);
  const [showSSN, setShowSSN] = useState(false);

  const handleClickShowSSN = () => setShowSSN((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const container = useRef();
  const [creditCheckError, setCreditCheckError] = useState(false);
  const {
    cart,
    reservationReturn,
    reservationStart,
    setReservationStart,
    setReservationReturn,
    selectedAdditionalServices,
    setSelectedAdditionalServices,
    setSelectedCompany,
    cartTotalValue,
    replaceCart,
    selectedCompany,
    mobileViewPort,
    language,
    FEATURE_PAYMENT_METHOD_SELECTOR,
    setLanguage,
  } = useContext(AppContext);

  const [reservationFailed, setReservationFailed] = useState(false);
  const [reservationCancelled, setReservationCancelled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [risk, setRisk] = useState({
    id: 0,
    key: "fullDeductible",
    offerPrice: 0,
  });
  const [snackbarMessage, setSnackbarMessage] = useState();
  const [voucher, setVoucher] = useState("");
  const [appliedVoucher, setAppliedVoucher] = useState();
  const [validatingVoucher, setValidatingVoucher] = useState(false);
  const [additionalServiceOffers, setAdditionalServiceOffers] = useState([]);
  const [additionalDistanceServiceOffers, setAdditionalDistanceServiceOffers] =
    useState([]);

  // TODO: CHECK THESE
  const [hasNormalServices, setHasNormalServices] = useState(false);
  const [hasDistanceServices, setHasDistanceServices] = useState(false);
  const [hasDeductibleServices, setHasDeductibleServices] = useState(false);
  const [paymentMode, setPaymentMode] = useState(PaymentModeEnum.now);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const printRef = useRef(null);

  // Confirmation state when returning from external link
  const [reservationId, setReservationId] = useState(null);
  const [expandedReservationStep, setExpandedReservationStep] = useState(
    "contact-information"
  );

  const [submittedReservation, setSubmittedReservation] = useState();
  const [loading, setLoading] = useState(false);
  const [reservationInfoError, setReservationInfoError] = useState(false);

  const reservation = cart;

  const [authFailed, setAuthFailed] = useState(false);
  const [noMatchSSN, setNoMatchSSN] = useState(false);

  const handleAdvanceStep = () => {
    formik.validateForm().then((e) => {
      if (Object.keys(e).length !== 0) {
        Object.keys(e).map((error) => formik.setFieldTouched(error, true));
      } else {
        gtagPageView(
          "4. Yhteystiedot",
          "/4-yhteystiedot",
          "step-4-form-submitted-done",
          selectedCompany.enableGTM
        );
        gtmAddShippingInfo(cart, selectedAdditionalServices, selectedCompany);

        if (formik?.values?.bypassAuthentication) {
          return setExpandedReservationStep("payment-section");
        }

        if (FEATURE_MUST_STRONG_AUTHENTICATE) {
          setDisableAuthenticationStep(false);
          setExpandedReservationStep("authentication");
        } else {
          setDisableAuthenticationStep(true);
          setExpandedReservationStep("payment-section");
        }
      }
    });
    //
  };

  const FEATURE_HIDE_VOUCHER = hideVouchers(selectedCompany?.group);

  // Hide option to select different driver
  const FEATURE_DISABLE_ADDITIONAL_DRIVER = useMemo(() => {
    for (let item of reservation) {
      if (item?.vehicle?.category?.typeId === "product-type-saunaboats") {
        return true;
      }
    }
    return disableAdditionalDriver(selectedCompany?.group);
  }, [selectedCompany, reservation]);

  const FEATURE_DISABLE_COMPANY_AS_CUSTOMER = useMemo(() => {
    return disableCustomerIsCompany(selectedCompany?.group);
  }, [selectedCompany]);

  // Set feature flags
  const FEATURE_ENABLE_PAYMENT = useMemo(() => {
    return paymentEnabled(process.env.REACT_APP_ENV, selectedCompany);
  }, [selectedCompany]);

  const FEATURE_MIN_INPUT = useMemo(() => {
    return enableMinimalCustomerInput(
      process.env.REACT_APP_ENV,
      selectedCompany?.group
    );
  }, [selectedCompany?.group]);

  const FEATURE_PRIVACY_POLICY = useMemo(() => {
    if (submittedReservation?.authStatus === "COMPLETED") return false;
    return enablePrivacyPolicy(
      process.env.REACT_APP_ENV,
      selectedCompany?.group
    );
  }, [selectedCompany?.group, submittedReservation]);

  // paymentMode must be onPickup when online payment not enabled
  useEffect(() => {
    if (!FEATURE_ENABLE_PAYMENT) {
      setPaymentMode(PaymentModeEnum.onPickup);
    }
  }, [FEATURE_ENABLE_PAYMENT]);

  // TODO: Show deposit payment option if this is true CU-3rj81fw
  const FEATURE_RESERVATION_DEPOSIT = useMemo(() => {
    return !!selectedCompany?.enableReservationDeposit;
  }, [selectedCompany]);

  const FEATURE_PAYMENT_ONLINE_ONLY = useMemo(() => {
    if (!FEATURE_ENABLE_PAYMENT) return false;
    // On this domain we always have payment online only
    const isToriDomain = window.location.hostname === "orhi.rent";
    if (
      isToriDomain ||
      ["COMPLETED_DEPOSIT", "PENDING_FULL"].includes(
        submittedReservation?.paymentStatus
      )
    ) {
      // Deposit payment is done and waiting for the full payment. Currently
      // payment on pickup is not supported on this state
      return true;
    }
    return !!selectedCompany?.enablePaymentOnlineOnly;
  }, [selectedCompany, FEATURE_ENABLE_PAYMENT, submittedReservation]);

  // Get label for payment on pickup
  const FEATURE_PAYMENT_ON_PICKUP_LABEL = useMemo(() => {
    const customLabel = paymentOnPickupLabel(selectedCompany?.group);
    if (customLabel) return customLabel;
    return translation.get(language, "Pay upon pick-up");
  }, [selectedCompany]);

  // Get label for payment on pickup
  const FEATURE_DIFFERENT_DRIVER_LABEL = useMemo(() => {
    const customLabel = differentDriverLabel(selectedCompany?.group);
    if (customLabel) return customLabel;
    return translation.get(language, "Different driver");
  }, [selectedCompany, language]);

  const showReturnBtn = useMemo(() => {
    return false; // Always hidden for now. Something wrong with the logic below, and the button is shown on the hosting url.
    const hiddenOrigin = process.env.REACT_APP_HOSTING_URL_CUSTOMER;
    if (hiddenOrigin === window.location.origin) return false;
    return true;
  }, []);

  const FEATURE_CREDITCHECK_TITLE = useMemo(() => {
    if (selectedCompany?.group === "vuokraapaku") {
      return (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>Luottotietojen tarkistus</span>
          <span>
            (asiakas ei voi vuokrata autoa, jos hänen luottotiedot ei ole
            kunnossa)
          </span>
        </div>
      );
    }
    return "Luottotietojen tarkistus";
  }, [selectedCompany?.group]);

  const FEATURE_CREDITCHECK_LABEL = useMemo(() => {
    if (selectedCompany?.creditCheck?.enabled && language === "fi") {
      return "Annan lupani luottotietojeni tarkastukseen, luottotietojen tarkastajana toimii vuokraamo ja palvelun tarjoajana Suomen Asiakastieto Oy.";
    }
    if (selectedCompany?.creditCheck?.enabled && language === "en") {
      return "I give my permission for my credit information to be checked, the credit information checker is the rental company and the service provider is Suomen Asiakastieto Oy.";
    }
    if (selectedCompany?.creditCheck?.enabled && language === "se") {
      return "Jag ger mitt tillstånd att min kreditupplysning kontrolleras, kreditupplysningsgranskaren är uthyrningsföretaget och tjänsteleverantören är Suomen Asiakastieto Oy.";
    }
    if (selectedCompany?.group === "nestesorsasalo" && language === "fi") {
      return "Luottotietoni saa tarkistaa";
    }

    if (selectedCompany?.group === "nestesorsasalo" && language === "en") {
      return "My credit information may be checked";
    }

    if (selectedCompany?.group === "nestesorsasalo" && language === "se") {
      return "Min kreditinformation kan komma att kontrolleras";
    }

    if (selectedCompany?.group === "pakunvuokraus" && language === "fi") {
      return "Luottotietoni saa tarkistaa. Mikäli luottotietoni eivät ole kunnossa, olen valmis maksamaan 500 € vakuusmaksun joka palautetaan vuokrauksen jälkeen.";
    }
    if (selectedCompany?.group === "pakunvuokraus" && language === "en") {
      return "My credit information may be checked. If my credit information is not in order, I am willing to pay a security deposit of 500 €, which will be refunded after the rental.";
    }
    if (selectedCompany?.group === "pakunvuokraus" && language === "se") {
      return "Min kreditinformation kan kontrolleras. Om min kreditinformation inte är i ordning, är jag villig att betala en säkerhetsdeposition på 500 €, vilken kommer att återbetalas efter hyresperiodens slut.";
    }

    if (selectedCompany?.group === "vuokraapaku" && language === "fi") {
      return "Asiakas hyväksyy, että hänen luottotiedot mahdollisesti tarkistetaan. Jos vuokraamo huomaa missä vaiheessa tahansa, että vuokraajalla tai kuljettajalla on luottotiedoissa maksuhäiriömerkintä, niin vuokraamolla on oikeus peruuttaa heti vuokraus ja asiakkaan on välittömästi palautettava auto tankattuna ja kuvat otettuna (ennen ja jälkeen). Vuokraamo ei palauta vuokrasummaa, sillä vuokrauksen ehtona on, että asiakkaalle ei ole luottotiedoissa häiriömerkintää, eikä hän tällöin olisi saanut ottaa autoa vuokralle";
    }
    if (selectedCompany?.group === "vuokraapaku" && language === "en") {
      return "The customer agrees that their credit information may be checked. If at any point during the rental period the rental company discovers that the renter or driver has a payment default entry in their credit information, the rental company has the right to immediately cancel the rental, and the customer must immediately return the car with a full tank of fuel and with pictures taken (before and after). The rental fee will not be refunded, as a condition for the rental is that the customer has no default entries in their credit information and would not have been allowed to rent the car in such a case.";
    }
    if (selectedCompany?.group === "vuokraapaku" && language === "se") {
      return "Kunden samtycker till att deras kreditinformation kan kontrolleras. Om hyresföretaget vid något tillfälle under hyresperioden upptäcker att hyrestagaren eller föraren har en betalningsanmärkning i sin kreditinformation, har hyresföretaget rätt att omedelbart avbryta uthyrningen, och kunden måste omedelbart återlämna bilen med en full tank bränsle och med tagna bilder (före och efter). Hyresavgiften återbetalas inte, eftersom en förutsättning för hyresavtalet är att kunden inte har några betalningsanmärkningar i sin kreditinformation och inte skulle ha fått hyra bilen i ett sådant fall.";
    } else {
      // default credit check label
      return translation.get(language, "Credit check approval label");
    }
  }, [selectedCompany?.group, language]);

  const FEATURE_DISABLE_CREDITCHECK = useMemo(() => {
    if (submittedReservation?.authStatus === "COMPLETED") return true;

    if (
      selectedCompany?.organizationId === "aaa9a975-fbcb-41b4-a2dd-3f404f6920a1"
    )
      return true; // disable credit check from organization SCANDIA RENT
    if (selectedCompany?.features?.disableCreditCheckBox) {
      return true
    }
    return disableCreditcheck(selectedCompany?.group);
  }, [
    selectedCompany?.group,
    selectedCompany?.organizationId,
    submittedReservation?.authStatus,
  ]);

  const FEATURE_SHOW_PORT_INSTRUCTION = useMemo(() => {
    return showPortInstruction(selectedCompany?.id);
  }, [selectedCompany]);
  const FEATURE_MUST_STRONG_AUTHENTICATE = selectedCompany?.enableMustSign;

  const FEATURE_ASK_SSN = useMemo(() => {
    // Hide SSN field if filled and confirmed. (API currently does not expose SSN )
    if (submittedReservation?.authStatus === "COMPLETED") return false;
    if (FEATURE_MUST_STRONG_AUTHENTICATE) {
      return true;
    }
    return askSsn(selectedCompany?.group);
  }, [
    submittedReservation,
    selectedCompany?.group,
    FEATURE_MUST_STRONG_AUTHENTICATE,
  ]);

  const FEATURE_ASK_DRIVER_SSN = useMemo(() => {
    // If selecting additional driver is disabled, this feature is disabled also.
    if (FEATURE_DISABLE_ADDITIONAL_DRIVER) return false;
    return askDriverSsn(selectedCompany?.group);
  }, [
    submittedReservation,
    FEATURE_DISABLE_ADDITIONAL_DRIVER,
    selectedCompany?.group,
  ]);

  const VISMA_PAY_MAX = useMemo(() => {
    return 600000;
  }, [selectedCompany?.group]);

  const discountedTotalPrice = useMemo(() => {
    const paymentStatus = submittedReservation?.paymentStatus;
    // If deposit has been paid but waiting for the rest, show the payment todo as total price.
    if (
      paymentStatus === "COMPLETED_DEPOSIT" ||
      paymentStatus === "PENDING_FULL"
    ) {
      return submittedReservation.paymentData.paymentTodo;
    }
    if (submittedReservation?.voucherData?.amount) {
      // Discount is already applied to the prices
      return cartTotalValue;
    }
    return calculateDiscountedPrice(
      cartTotalValue,
      appliedVoucher?.amount || 0
    );
  }, [cartTotalValue, appliedVoucher, submittedReservation]);

  // See if deposit option should be visible for the user. true / false
  const depositVisible = useMemo(() => {
    if (!FEATURE_RESERVATION_DEPOSIT) return false;
    // Require deposit settings to be set
    const deposit = selectedCompany?.reservationDeposit;
    if (!deposit) return false;
    if (!(deposit.amount && deposit.reminderBefore && deposit.paymentBefore))
      return false;
    // Deposit cannot be initiated in following states
    if (
      ["CANCELLED", "COMPLETED", "COMPLETED_DEPOSIT", "PENDING_FULL"].includes(
        submittedReservation?.paymentStatus
      )
    )
      return false;

    return true;
  }, [selectedCompany, submittedReservation, FEATURE_RESERVATION_DEPOSIT]);

  // See if Reservation is far enough considering paymentBefore setting
  const depositPossible = useMemo(() => {
    if (!depositVisible) return false;
    const MILLISECONDS_IN_DAY = 86400000;
    const paymentBeforeMs =
      MILLISECONDS_IN_DAY * selectedCompany.reservationDeposit.paymentBefore;
    // Use correct variable for getting startTime
    const time = submittedReservation
      ? submittedReservation.startTime
      : reservationStart;
    const startTimeMs = new Date(time).getTime();
    const paymentDeadlineMs = startTimeMs - paymentBeforeMs;
    const nowMs = new Date().getTime();
    // Can't use deposit if startTime is closer than paymentBefore deadline
    if (startTimeMs - nowMs < paymentBeforeMs) {
      return false;
    }
    // Deposit is not an option, if it is greater that the total price
    if (discountedTotalPrice < selectedCompany?.reservationDeposit?.amount) {
      return false;
    }
    return true;
  }, [
    selectedCompany,
    submittedReservation,
    FEATURE_RESERVATION_DEPOSIT,
    depositVisible,
    discountedTotalPrice,
  ]);

  // Show deposit related info when deposit is paid, but waiting for the full payment
  const showDepositInfo = useMemo(() => {
    if (!submittedReservation) return false;
    if (
      submittedReservation.paymentStatus === "COMPLETED_DEPOSIT" ||
      submittedReservation.paymentStatus === "PENDING_FULL"
    ) {
      return true;
    }
    return false;
  }, [submittedReservation]);

  const disableFields = useMemo(() => {
    if (
      submittedReservation?.paymentStatus === "COMPLETED" ||
      submittedReservation?.paymentStatus === "CANCELLED"
    ) {
      return true;
    }
    return false;
  }, [submittedReservation]);

  const validate = (values) => {
    const errors = {};
    const GENERAL_ERROR_MSG = translation.get(
      language,
      "Fill this field label"
    );

    if (!values.firstName) {
      errors.firstName = translation.get(language, "Fill this field label");
    }
    if (!values.lastName) {
      errors.lastName = translation.get(language, "Fill this field label");
    }

    if (!FEATURE_MIN_INPUT) {
      if (!values.address) {
        errors.address = translation.get(language, "Fill this field label");
      }

      if (!values.postalCode) {
        errors.postalCode = translation.get(language, "Fill this field label");
      }

      if (!values.city) {
        errors.city = translation.get(language, "Fill this field label");
      }
    }

    const phoneFields = [
      {
        field: "phone",
        countryCodeField: "phoneCountryCode",
        error: "Tarkista puhelinnumero",
        isEnabled: true,
      },
      {
        field: "driverPhone",
        countryCodeField: "driverPhoneCountryCode",
        error: "Tarkista kuljettajan puhelinnumero",
        isEnabled: values.differentDriver,
      },
      {
        field: "additionalDriverPhone",
        countryCodeField: "additionalDriverPhoneCountryCode",
        error: "Tarkista lisäkuljettajan puhelinnumero",
        isEnabled: values.additionalDriver,
      },
    ];

    phoneFields.forEach(({ field, countryCodeField, error, isEnabled }) => {
      // Is the field empty
      if (isEnabled && !values[field]) {
        errors[field] = translation.get(language, "Fill this field label");
      }
      // If the phone field and the corresponding countryCode field are not empty, do the validation
      if (isEnabled && values[field] && values[field] !== "") {
        try {
          const phoneNumber = parsePhoneNumberWithError(
            values[countryCodeField] + values[field]
          );
          const validNumber =
            isValidPhoneNumber(phoneNumber?.number, phoneNumber?.country) ??
            false;

          if (!validNumber) {
            errors[field] = error;
          }
        } catch (e) {
          if (e instanceof ParseError) {
            // Not a phone number, non-existent country, etc.
            //console.log(
            //"Error parsing the phone number of field " + field + ":",
            //e.message,
            //"value:",
            //values[countryCodeField],
            //values[field]
            //);
            errors[field] = error;
          } else {
            throw error;
          }
        }
      }
    });

    if (!values.email) {
      errors.email = translation.get(language, "Fill this field label");
    }

    if (
      values.email &&
      !/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
        values.email
      )
    ) {
      errors.email = translation.get(language, "Check email label");
    }
    if (!values.emailRepeat) {
      errors.emailRepeat = translation.get(language, "Fill this field label");
    }
    if (
      values.emailRepeat &&
      !/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
        values.emailRepeat
      )
    ) {
      errors.emailRepeat = translation.get(language, "Check email label");
    }
    if (values.emailRepeat && values.email != values.emailRepeat) {
      errors.emailRepeat = translation.get(
        language,
        "Non matching emails label"
      );
    }

    // Do not allow altering ssn after authenticated

    if (
      submittedReservation?.authStatus !== "COMPLETED" &&
      !values.bypassAuthentication
    ) {
      if (FEATURE_MUST_STRONG_AUTHENTICATE && !values.ssn) {
        errors.ssn = translation.get(language, "Fill this field label");
      }
      if (
        values.ssn &&
        !/^(0[1-9]|[12]\d|3[01])(0[1-9]|1[0-2])(\d\d[-+A])\d{3}[\dA-Z]$/.test(
          values.ssn
        )
      ) {
        errors.ssn = translation.get(language, "SSN error label");
      }
    }

    if (FEATURE_ASK_DRIVER_SSN && values.differentDriver && !values.driverSSN) {
      errors.driverSSN = translation.get(language, "Fill this field label");
    }

    // If authStatus COMPLETED, user has already previously accepted these.

    if (submittedReservation?.authStatus !== "COMPLETED") {
      if (
        !values.acceptTerms &&
        expandedReservationStep === "authentication" &&
        FEATURE_MUST_STRONG_AUTHENTICATE
      ) {
        errors.acceptTerms = translation.get(language, "Accept terms error");
      }

      if (
        !values.acceptTerms &&
        !FEATURE_MUST_STRONG_AUTHENTICATE &&
        expandedReservationStep === "contact-information"
      ) {
        errors.acceptTerms = translation.get(language, "Accept terms label");
      }
      if (
        expandedReservationStep === "completed-reservation" &&
        !values.acceptTerms
      ) {
        errors.acceptTerms = translation.get(language, "Accept terms error");
      }

      if (FEATURE_PRIVACY_POLICY && !values.acceptPrivacyPolicy) {
        errors.acceptPrivacyPolicy = translation.get(
          language,
          "Accept privacy policy label"
        );
      }

      if (FEATURE_SHOW_PORT_INSTRUCTION && !values.acceptPortTerms) {
        errors.acceptPortTerms = translation.get(
          language,
          "Accept port terms label"
        );
      }

      if (!values.acceptCreditCheck && !FEATURE_DISABLE_CREDITCHECK) {
        errors.acceptCreditCheck = translation.get(
          language,
          "Accept credit check label"
        );
      }
    }

    if (
      additionalDistanceServiceOffers.filter((s) => values[s.id]).length > 1
    ) {
      errors.distanceServices = translation.get(
        language,
        "Distance service error label"
      );
    }
    if (values.exportLicense && !values.exportLicenseDetails) {
      errors.exportLicenseDetails = GENERAL_ERROR_MSG;
    }

    if (values.isCompany) {
      if (!values.companyName) {
        errors.companyName = GENERAL_ERROR_MSG;
      }
    }

    if (values.differentDriver) {
      if (!values.driverName) {
        errors.driverName = GENERAL_ERROR_MSG;
      }

      if (!values.driverAddress) {
        errors.driverAddress = GENERAL_ERROR_MSG;
      }

      if (!values.driverPostalCode) {
        errors.driverPostalCode = GENERAL_ERROR_MSG;
      }

      if (!values.driverCity) {
        errors.driverCity = GENERAL_ERROR_MSG;
      }

      if (!values.driverEmail) {
        errors.driverEmail = GENERAL_ERROR_MSG;
      }

      if (values.additionalDriver) {
        if (!values.additionalDriverName) {
          errors.additionalDriverName = GENERAL_ERROR_MSG;
        }
      }
    }
    if (
      FEATURE_ENABLE_PAYMENT &&
      showPaymentSection &&
      (paymentMode === PaymentModeEnum.now ||
        paymentMode === PaymentModeEnum.now_deposit) &&
      !values.paymentMethod &&
      expandedReservationStep === "payment-section"
    ) {
      errors.paymentMethod = translation.get(language, "Payment method error");
    }
    return errors;
  };
  const fixedLanguage =
    submittedReservation?.language ?? getFixedLanguageSet(language);

  const formik = useFormik({
    initialValues: INITIAL_FORM_DATA,
    validate,
    onSubmit,
  });

  const showPaymentSection = useMemo(() => {
    if (!submittedReservation) return true; // Reservation has not been created yet
    if (
      FEATURE_MUST_STRONG_AUTHENTICATE &&
      submittedReservation?.authStatus !== "COMPLETED" &&
      !formik?.values?.bypassAuthentication
    ) {
      return false;
    }
    if (
      submittedReservation?.paymentStatus === "COMPLETED" ||
      submittedReservation?.paymentStatus === "CANCELLED" ||
      !submittedReservation?.paymentStatus
    ) {
      return false;
    }
    return true;
  }, [
    FEATURE_MUST_STRONG_AUTHENTICATE,
    submittedReservation,
    formik?.values?.bypassAuthentication,
  ]);

  const fetchCompany = async (companyId) => {
    const response = await API.graphql(
      graphqlOperation(getCompany, {
        id: companyId,
      })
    );
    setSelectedCompany(response.data.getCompany);
    return response.data.getCompany;
  };

  //TODO: Change to useMemo
  const fetchAdditionalServices = async () => {
    const services = selectedAdditionalServices;

    const distanceServices = services.filter((s) => s.category === "distance");

    setAdditionalServiceOffers(services);
    setAdditionalDistanceServiceOffers(distanceServices);
    setHasNormalServices(services.some((s) => s.category === "normal"));
    setHasDistanceServices(services.some((s) => s.category === "distance"));
    setHasDeductibleServices(services.some((s) => s.category === "deductible"));
  };

  const fetchValidatedVoucher = async (code) => {
    const response = await API.graphql(
      // Vehicle is used to get the group, and it should always be the same on vehicles on one reservation
      graphqlOperation(validateVoucher, {
        code: code,
        group: reservation[0].vehicle?.group,
        organizationId: reservation[0].vehicle?.organizationId,
      })
    );

    return response.data.validateVoucher;
  };

  const selectedServices = additionalServiceOffers.filter(
    (serviceOffer) => formik.values[serviceOffer.id]
  );

  // Price of distance and deductible services is by default multiplied by the amount of vehicles
  const getPriceMultiplier = (service) => {
    if (service.quantity > 1) {
      return service.quantity;
    }
    if (["deductible", "distance"].includes(service?.category)) {
      return reservation.reduce((t, vehicle) => t + vehicle.quantity, 0);
    }
    return 1;
  };

  const applyVoucher = async () => {
    setValidatingVoucher(true);
    try {
      const voucherData = await fetchValidatedVoucher(voucher);

      if (voucherData) {
        setAppliedVoucher(voucherData);
      } else {
        setSnackbarMessage(translation.get(language, "Invalid voucher label"));
      }
    } catch (e) {
      captureError("Validating voucher", "VOUCHER_VALIDATION_FAILED", e);
      setSnackbarMessage(translation.get(language, "Something went wrong"));
    }
    setValidatingVoucher(false);
  };

  const doCancelReservation = async () => {
    try {
      const response = await API.graphql(
        graphqlOperation(cancelReservationCustomer, {
          reservationId: reservationId,
        })
      );
      const success = response.data.cancelReservationCustomer.success;
      if (success) {
        setReservationCancelled(true);
      } else {
        captureError(
          "Canceling reservation success not true",
          "CANCEL_RESERVATION_FAILED",
          ""
        );
        setSnackbarMessage("Jokin meni vikaan");
      }
    } catch (e) {
      captureError("Canceling reservation", "CANCEL_RESERVATION_FAILED", e);
      setSnackbarMessage("Jokin meni vikaan");
    }
  };

  const hasMultipleDeductibles = Boolean(
    additionalServiceOffers.find((s) => s.key === "halvedDeductible") &&
      additionalServiceOffers.find((s) => s.key === "noDeductible")
  );

  // Visma Pay doesn't allow 0€ charges, and there is also a upper limit per customer
  useEffect(() => {
    if (FEATURE_ENABLE_PAYMENT) {
      if (discountedTotalPrice === 0) {
        setPaymentMode(PaymentModeEnum.onPickup);
      }
      if (
        discountedTotalPrice > 0 &&
        discountedTotalPrice < VISMA_PAY_MAX &&
        FEATURE_PAYMENT_ONLINE_ONLY
      ) {
        setPaymentMode(PaymentModeEnum.now);
      }
      if (discountedTotalPrice > VISMA_PAY_MAX) {
        setPaymentMode(PaymentModeEnum.onPickup);
        setSnackbarMessage(
          `${translation.get(
            language,
            "Online payment not available"
          )} ${centsToLocalString(VISMA_PAY_MAX, true)}`
        );
      }
    }
  }, [
    appliedVoucher,
    FEATURE_PAYMENT_ONLINE_ONLY,
    FEATURE_ENABLE_PAYMENT,
    discountedTotalPrice,
    VISMA_PAY_MAX,
  ]);

  const updateReservation = async (values, paymentStr) => {
    const name = getName(values);

    const parsedPhone = getPhone(values, "phone");
    const parsedDriverPhone = getPhone(values, "driverPhone");
    const parsedAdditionalDriverPhone = getPhone(
      values,
      "additionalDriverPhone"
    );

    const reservationInfo = {
      id: reservationId,
      name: name,
      address: values.address,
      postalCode: values.postalCode,
      city: values.city,
      phone: parsedPhone,
      email: values.email,
      externalNotes: values.externalNotes,
      differentDriver: values.differentDriver,
      driverName: values.driverName,
      driverAddress: values.driverAddress,
      driverPostalCode: values.driverPostalCode,
      driverCity: values.driverCity,
      driverPhone: parsedDriverPhone,
      driverEmail: values.driverEmail,
      additionalDriver: values.additionalDriver,
      additionalDriverName: values.additionalDriverName,
      additionalDriverPhone: parsedAdditionalDriverPhone,
      isCompany: values.isCompany,
      voucherCode: appliedVoucher?.code,
      companyName: values.companyName,
      companyBusinessId: values.companyBusinessId,
      companyBillingRef: values.companyBillingRef,
      companyBillingAddress: values.companyBillingAddress,
    };

    if (values.differentDriver && FEATURE_ASK_DRIVER_SSN) {
      reservationInfo.driverSSN = values.driverSSN;
    }
    if (submittedReservation?.authStatus !== "COMPLETED") {
      reservationInfo.ssn = values.ssn;
    }
    try {
      const response = await API.graphql(
        graphqlOperation(setReservationInfo, {
          reservation: reservationInfo,
          paymentMode: paymentStr,
          bypassAuthentication: values?.bypassAuthentication,
        })
      );
      if (response.data.setReservationInfo.errorMsg) {
        console.log(
          "reservationInfo error:",
          response.data.setReservationInfo.errorMsg
        );
        return { error: response.data.setReservationInfo.errorMsg };
      }
    } catch (e) {
      console.error("Failure at setReservationInfo", e);
      captureError("setReservationInfo", "SET_RESERVATION_INFO", e);
      setSnackbarMessage(
        translation.get(language, "Failed creating a reservation")
      );
      throw new Error("setReservationInfo failed");
    }
  };

  const teliaAuthentication = (id) => {
    console.log("Redirect to telia");
    console.log(id);
    let BASE_URL = process.env.REACT_APP_TELIA_URL;
    let REST_BASE_URL = process.env.REACT_APP_REST_BASE_URL;
    let CLIENT_ID = process.env.REACT_APP_TELIA_CLIENT_ID;
    if (process.env.REACT_APP_ENV === "local") {
      // Use staging telia URL by default on local development
      console.log("Local env, use hard coded development URLs");
      BASE_URL = "https://tunnistus-pp.telia.fi";
      REST_BASE_URL =
        "https://hj8n7xjzp4.execute-api.eu-west-1.amazonaws.com/devtest";
      CLIENT_ID = "1c04ed14-92d4-4cf6-a9ab-eb4342cea7db";
    }
    const url = `${BASE_URL}/uas/oauth2/authorization?scope=openid&response_type=code&client_id=${CLIENT_ID}&redirect_uri=${REST_BASE_URL}/teliaId/callback?data=${
      window.location.origin + window.location.pathname
    },${id}`;
    window.location.replace(url);
  };

  const doAuthPayment = async (reservationId, values) => {
    const paymentMethod = paymentMethods.find((method) => {
      return method.name === values.paymentMethod;
    });
    try {
      const response = await API.graphql(
        graphqlOperation(authPayment, {
          reservationId: reservationId,
          paymentMethod: paymentMethod,
          origin: window.location.origin + window.location.pathname,
        })
      );
      return response.data.authPayment;
    } catch (e) {
      captureError("Auth payment failed", "AUTH_PAYMENT_FAILED", e);
      navigate("/vahvistus", {
        state: {
          ORDER_NUMBER: reservationId,
          group: selectedCompany?.group,
        },
      });
    }
  };

  /* Different submit strategies
   * 1) Only Upsert // Maksu noudettaessa
   * 2) Upsert & Visma Pay // Maksa  heti
   * 3) Upsert & TeliaId // Vahva tunnistautuminen
   * 4) Update & Visma Pay // Maksu vahvan tunnistautumisen jälkeen / täydennyslinkki
   * 5) Update & Telia Id // Vahva tunnistautuminen epäonnistuneen tunnistuksen jälkeen? / täydennyslinkki
   */
  async function onSubmit(values) {
    setSubmitting(true);
    const submitVariant = values.submitVariant;
    if (!submitVariant) {
      console.error("No submitVariant set, should not happen");
      return;
    }
    try {
      if (submitVariant === 4) {
        // Only init Visma Pay, Reservation is already created
        // Last chance to change paymentMode (deposit for example)
        const result = await updateReservation(values, paymentMode);
        if (result?.error) {
          setReservationInfoError(result.error);
          setLoading(false);
          return false;
        }
        const authPaymentResult = await doAuthPayment(reservationId, values);
        if (authPaymentResult.result === 0) {
          window.location.replace(
            "https://www.vismapay.com/pbwapi/token/" + authPaymentResult.token
          );
        } else {
          navigate("/vahvistus", {
            state: {
              ORDER_NUMBER: reservationId,
            },
          });
        }
        return;
      }
      if (submitVariant === 5) {
        // Only init Telia Id, Reservation is already created
        await updateReservation(values);
        return teliaAuthentication(reservationId); // TODO: Add to reservationInfo return
      }

      // Update reservation, set paymentStatus to CANCELLED (set PENDING at teliaAuth) and send email confirmation
      if (submitVariant === 6) {
        await updateReservation(values);
        const result = await API.graphql(
          graphqlOperation(setReservationPaymentStatus, {
            reservationId: reservationId,
          })
        );
        if (!result.data.setReservationPaymentStatus.emailConfirmationSuccess) {
          Sentry.captureMessage("Send confirmation email failed", {
            tags: {
              code: "CONFIRMATION_EMAIL_FAILED",
              destinationEmail: result.destinationEmail,
            },
          });
        }
        return navigateToConfirmation(result.data.setReservationPaymentStatus);
      }

      // Create reservation since all variants after that requires it.
      const result = await handleUpsert(values);
      if (!result.success) {
        // Reservation creation was not succesful
        return;
      }
      if (submitVariant === 1) {
        // Create Reservation only
        navigateToConfirmation(result);
      }
      if (submitVariant === 2) {
        // Init Visma Pay process
        if (
          FEATURE_ENABLE_PAYMENT &&
          (paymentMode === PaymentModeEnum.now ||
            paymentMode === PaymentModeEnum.now_deposit)
        ) {
          const authPaymentResult = await doAuthPayment(
            result.reservationId,
            values
          );
          if (authPaymentResult.result === 0) {
            window.location.replace(
              "https://www.vismapay.com/pbwapi/token/" + authPaymentResult.token
            );
          } else {
            navigate("/vahvistus", {
              state: {
                ORDER_NUMBER: result.reservationId,
              },
            });
          }
          return;
        }
      }
      if (submitVariant === 3) {
        // Init Telia Id process
        teliaAuthentication(result.reservationId);
      }
      console.log("Nothing happens?");
      return;
    } catch (e) {
      console.log("catch", e);
      captureError(
        "Reservation onSubmit failed",
        "CREATE_RESERVATION_FAILED",
        e
      );
      setSnackbarMessage(translation.get(language, "Something went wrong"));
    }
    setSubmitting(false);
  }

  const navigateToConfirmation = (result) => {
    navigate("/vahvistus", {
      state: {
        emailConfirmationSuccess: result.emailConfirmationSuccess,
        reservationId: result.reservationId,
        group: selectedCompany?.group,
        gtm: selectedCompany.enableGTM,
      },
    });
  };

  const handleUpsert = async (values) => {
    const services = selectedAdditionalServices.map((serviceOffer) => ({
      key: serviceOffer.id,
      offerPrice: calculateDiscountedPrice(
        serviceOffer.offerPrice,
        appliedVoucher?.amount || 0
      ),
      details: serviceOffer?.details || "",
      quantity: serviceOffer.quantity || 1,
    }));

    if (risk.id !== 0) {
      services.push({
        key: risk.key,
        offerPrice: calculateDiscountedPrice(
          risk.offerPrice * getPriceMultiplier(risk),
          appliedVoucher?.amount || 0
        ),
      });
    }
    const reservationVehicles = cart.map((c) => {
      return {
        id: c.vehicle.id,
        quantity: c.quantity,
        offerPrice: calculateDiscountedPrice(
          c.productPrice,
          appliedVoucher?.amount || 0
        ),
      };
    });

    const parsedPhone = getPhone(values, "phone");
    const parsedDriverPhone = getPhone(values, "driverPhone");
    const parsedAdditionalDriverPhone = getPhone(
      values,
      "additionalDriverPhone"
    );

    const name = getName(values);

    let payment = paymentMode;
    const input = {
      group: reservation[0].vehicle.group ?? undefined,
      organizationId: reservation[0].vehicle.organizationId ?? undefined,
      orgBusinessId: reservation[0].vehicle.orgBusinessId ?? undefined,
      reservationVehicles: reservationVehicles,
      companyId: reservation[0].vehicle.companyId,
      startTime: zonedTimeToUtc(reservationStart, selectedCompany.timezone),
      returnTime: zonedTimeToUtc(reservationReturn, selectedCompany.timezone),
      name: name,
      address: values.address,
      postalCode: values.postalCode,
      city: values.city,
      phone: parsedPhone,
      email: values.email,
      externalNotes: values.externalNotes,
      ssn: values.ssn,
      additionalServiceOffers: services,
      voucherCode: appliedVoucher?.code,
      differentDriver: values.differentDriver,
      driverName: values.driverName,
      driverAddress: values.driverAddress,
      driverPostalCode: values.driverPostalCode,
      driverCity: values.driverCity,
      driverPhone: parsedDriverPhone,
      driverEmail: values.driverEmail,
      additionalDriver: values.additionalDriver,
      additionalDriverName: values.additionalDriverName,
      additionalDriverPhone: parsedAdditionalDriverPhone,
      isCompany: values.isCompany,
      companyName: values.companyName,
      companyBusinessId: values.companyBusinessId,
      companyBillingRef: values.companyBillingRef,
      companyBillingAddress: values.companyBillingAddress,
      language: fixedLanguage,
    };
    if (values.differentDriver && FEATURE_ASK_DRIVER_SSN) {
      input.driverSSN = values.driverSSN;
    }
    const result = await doUpsertReservation(
      input,
      payment,
      values?.bypassAuthentication
    );
    if (result.code != null) {
      Sentry.captureMessage("Reservation failed", {
        tags: {
          code: result.code,
        },
      });
      if (result.code === "VEHICLE_NOT_AVAILABLE") {
        setReservationFailed(true);
      } else {
        console.log("doUpsertReservation", result);
        console.log("doUpsertReservation", reservation);
        setSnackbarMessage(translation.get(language, "Something went wrong"));
      }
    } else {
      // emailConfirmationSuccess false at this stage is expected if customer is paying online
      if (
        (!result.emailConfirmationSuccess && !FEATURE_ENABLE_PAYMENT) ||
        (!result.emailConfirmationSuccess &&
          FEATURE_ENABLE_PAYMENT &&
          paymentMode === PaymentModeEnum.onPickup)
      ) {
        Sentry.captureMessage("Send confirmation email failed", {
          tags: {
            code: "CONFIRMATION_EMAIL_FAILED",
            destinationEmail: values.email,
          },
        });
      }
    }
    return result;
  };

  useEffect(() => {
    fetchAdditionalServices();
    container?.current?.scrollIntoView({ behavior: "smooth" });
  }, [container]);

  // All Vehicles in cart should have the same companyId
  // BUG: THIS CAUSES A MELTDOWN,
  // Warning: useEffect must not return anything besides a function, which is used for clean-up. You returned: [object Object]
  /*   useEffect(() => {
    if (cart.length === 0) {
      handleActiveStep(0);
      return <RouterLink to="/" />;
    }
  }, [reservation]);
 */

  const [openTooltip, setOpenTooltip] = useState(false);
  const handleTooltipClose = () => setOpenTooltip(false);
  const handleTooltipOpen = () => setOpenTooltip(true);

  useEffect(() => {
    if (reservationId) {
      fetchReservation();
    }
  }, [reservationId]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    // Get reservation id from query param, if doesnt exist get from state
    const reservationId = query.get("reservationId");
    if (!!reservationId) {
      setReservationId(reservationId);
    }
    const error = query.get("error");
    if (error === "ssn-no-match") {
      setNoMatchSSN(true);
      setSnackbarMessage(translation.get(language, "Auth failed"));
      setExpandedReservationStep("contact-information");
    }
    if (error === "access_denied") {
      setAuthFailed(true);
      setExpandedReservationStep("contact-information");
    }
    if (error === "unknown") {
      setAuthFailed(true);
      setCreditCheckError(true);
    }
  }, []);

  // Returning to confirmation state

  // TODO: Figure out what is happening here? Old code / WIP?
  useEffect(() => {
    if (submittedReservation) {
      container.current.scrollIntoView();
      const query = new URLSearchParams(location.search);

      // If ORDER_NUMBER query param exists, payment not succesful
      if (query.get("ORDER_NUMBER") || location.state?.ORDER_NUMBER) {
        // Set failedOrderId to trigger PaymentFailure, but also reservationId.
        // It is possible to get to Success from PaymentFailure, so we need reservationId.
        const orderNumber =
          query.get("ORDER_NUMBER") || location.state?.ORDER_NUMBER;

        setReservationId(orderNumber);

        setExpandedReservationStep("completed-reservation");
      } else {
        setExpandedReservationStep("completed-reservation");
        // Get reservation id from query param, if doesnt exist get from state
        const reservationId = query.get("reservationId");
        if (!!reservationId) {
          setReservationId(reservationId);
        } else {
          setReservationId(location.state?.reservationId);
        }
      }
      if (submittedReservation?.authStatus === "PENDING_MUST") {
        formik.setErrors("ssn", true);
        formik.setFieldTouched("ssn", true);
      }
    }
  }, []);

  const fetchReservation = async () => {
    setLoading(true);
    try {
      const response = await API.graphql(
        graphqlOperation(getReservationInfo, {
          reservationId: reservationId,
        })
      );
      const reservationInfo = response.data.getReservationInfo;
      if (reservationInfo.errorMsg) {
        console.log("reservationInfo error:", reservationInfo.errorMsg);
        setReservationInfoError(reservationInfo.errorMsg);
        setLoading(false);
        return;
      }
      setSubmittedReservation(reservationInfo);

      // For now only shown as separate fields here, name is still stored in a single field
      const delimiter = " ";
      const splitArray = reservationInfo.name?.split(delimiter) ?? ["", ""];
      const firstName = splitArray[0].trim();
      const lastName = splitArray.slice(1).join(delimiter).trim(); // Do this in case there is extra spaces. eg. "First   Second".

      formik.setValues({
        ...INITIAL_FORM_DATA,
        ...response.data.getReservationInfo,
        acceptCreditCheck: true,
        acceptTerms: true,
        firstName: firstName,
        lastName: lastName,
        emailRepeat: response.data.getReservationInfo.email,
      });
      if (!response.data.getReservationInfo.ssn) {
        formik.setFieldValue("ssn", "");
      }
      if (response.data.getReservationInfo.voucherData) {
        setAppliedVoucher(response.data.getReservationInfo.voucherData);
      }
      const newCart = response.data.getReservationInfo.vehicles.map((v) => {
        return {
          quantity: 1,
          productPrice: v.price,
          offerPrice: v.price,
          vehicle: {
            ...v,
          },
        };
      });
      replaceCart(newCart);
      const services = response.data.getReservationInfo.services.map((s) => {
        return {
          ...s,
          offerPrice: s.price,
        };
      });
      setSelectedAdditionalServices(services);
      const company = await fetchCompany(
        response.data.getReservationInfo.companyId
      );
      setReservationStart(
        utcToZonedTime(
          new Date(response.data.getReservationInfo.startTime),
          company.timezone
        )
      );
      setReservationReturn(
        utcToZonedTime(
          new Date(response.data.getReservationInfo.returnTime),
          company.timezone
        )
      );

      if (response?.data?.getReservationInfo?.language) {
        const lang = getFixedLanguage(
          response.data.getReservationInfo.language
        );
        setLanguage(lang);
      }

      setLoading(false);
    } catch (e) {
      console.error("Failed to get info", e);
      setReservationInfoError(true);
      setLoading(false);
    }
  };

  function PriceRow({ label, price, variant, lastRow }) {
    const { language } = useContext(AppContext);
    return (
      <Grid
        container
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "15px",
        }}
      >
        <Grid item>
          <Typography variant={variant}>{label}</Typography>
        </Grid>
        <Grid item style={{ textAlign: "right" }} sx={{ position: "relative" }}>
          <Typography variant={variant} noWrap>
            {centsToLocalString(price, true)}
          </Typography>
          {lastRow && (
            <Typography
              variant="subtitle2"
              style={{
                color: theme.palette.primary.mainText,
              }}
              sx={{ position: "absolute", top: "30px", right: 0 }}
            >
              {translation.get(language, "Inc vat")}
            </Typography>
          )}
        </Grid>
      </Grid>
    );
  }

  // Printing contract has some bugs, This is replaced by a dialog for now.
  /*  const doPrintContract = () => {
    console.log("Print contract");
    print(
      <PrintContract
        formik={formik}
        selectedAdditionalServices={selectedAdditionalServices}
        startTime={startTime}
        returnTime={returnTime}
        appliedVoucher={appliedVoucher}
        reservation={reservation}
        cart={cart}
        selectedCompany={selectedCompany}
      />
    );
  };
 */

  const [openContractDialog, setOpenContractDialog] = useState(false);
  const doPrintContract = () => {
    setOpenContractDialog(true);
  };

  useEffect(() => {
    container.current.scrollIntoView();
    const query = new URLSearchParams(location.search);

    // If ORDER_NUMBER query param exists, payment not succesful
    if (query.get("ORDER_NUMBER") || location.state?.ORDER_NUMBER) {
      // Set failedOrderId to trigger PaymentFailure, but also reservationId.
      // It is possible to get to Success from PaymentFailure, so we need reservationId.
      const orderNumber =
        query.get("ORDER_NUMBER") || location.state?.ORDER_NUMBER;

      setReservationId(orderNumber);
    } else {
      // Get reservation id from query param, if doesnt exist get from state
      const reservationId = query.get("reservationId");
      if (!!reservationId) {
        setReservationId(reservationId);
      } else {
        setReservationId(location.state?.reservationId);
      }
    }
  }, []);

  // When contact info has been filled and authentication is required, customer is redirected to the service.

  // After returning from the authentication, customer is redirected to the payment-section
  useEffect(() => {
    if (submittedReservation?.authStatus === "COMPLETED")
      setExpandedReservationStep("payment-section");

    if (submittedReservation) {
      formik.setFieldValue("submitVariant", submitVariantEnum.authOnly);
    } else {
      formik.setFieldValue("submitVariant", submitVariantEnum.upsertAndAuth);
    }
  }, [submittedReservation?.authStatus]);

  // Visma Pay doesn't allow transactions over certain treshold.
  // If only online payment activated, tell user to contact dealer
  if (FEATURE_PAYMENT_ONLINE_ONLY && discountedTotalPrice > VISMA_PAY_MAX) {
    return (
      <Container
        maxWidth="sm"
        style={{ paddingBottom: "10vh", padding: 0 }}
        ref={container}
      >
        <Card>
          <CardContent>
            <Grid container>
              <Grid item xs={12}>
                <Typography variant="h5">
                  {`${translation.get(
                    language,
                    "Contact the dealer"
                  )} ${translation.get(
                    language,
                    "Online payment not available"
                  )} ${centsToLocalString(VISMA_PAY_MAX, true)} `}
                </Typography>
              </Grid>
            </Grid>

            <Grid container item xs={12} justifyContent="space-between"></Grid>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container ref={container}>
        <Grid container direction="column" alignItems="center">
          <Typography variant="h2" gutterBottom>
            {translation.get(language, "Loading reservation")}
          </Typography>
          <CircularProgress style={{ height: "2em", width: "2em" }} />
        </Grid>
      </Container>
    );
  }

  if (reservationCancelled) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography variant="h4">Varauksen peruuttaminen onnistui!</Typography>
        {showReturnBtn && (
          <Button
            style={{ marginTop: "1rem" }}
            variant="contained"
            color="primary"
            onClick={() => navigate("/")}
          >
            Palaa etusivulle
          </Button>
        )}
      </div>
    );
  }

  if (reservationInfoError) {
    let errorMsg = translation.get(language, "Something went wrong 2");
    return (
      <Container
        maxWidth="sm"
        ref={container}
        style={{ paddingBottom: "10vh" }}
      >
        <AlreadyReservedDialog error={reservationInfoError} />
      </Container>
    );
  }

  if (creditCheckError) {
    let errorMsg = translation.get(language, "Contact the dealer");
    return (
      <Container
        maxWidth="sm"
        ref={container}
        style={{ paddingBottom: "10vh" }}
      >
        <AlreadyReservedDialog error={"unknown"} />
      </Container>
    );
  }

  return (
    <Container
      maxWidth={mobileViewPort ? false : "lg"}
      ref={container}
      sx={{
        marginTop: "25px",
        padding: "0px",
        backgroundColor: theme.palette.primary.background,
      }}
    >
      <Grid
        container
        sx={{
          flexDirection: mobileViewPort ? "column" : "row",
        }}
      >
        <Grid
          item
          xs={12}
          sm={12}
          md={6}
          style={{
            borderStyle: mobileViewPort ? "none" : "solid",
            borderTopWidth: 0,
            borderBottomWidth: 0,
            borderLeftWidth: 0,
            borderRightWidth: mobileViewPort ? "none" : 1,
            borderColor: "#efefef",
            paddingRight: !mobileViewPort ? 10 : undefined,
          }}
        >
          <Box
            sx={{ display: "flex", flexDirection: "column", padding: "5px" }}
          >
            <Box>
              <OrderSummary
                discountedTotalPrice={discountedTotalPrice}
                showPrice={!submittedReservation?.disablePricesInPrintouts}
              />
            </Box>
            {submittedReservation?.allowCancellation && (
              <Box sx={{ marginTop: 5 }}>
                <Grid
                  container
                  xs={12}
                  justify="center"
                  direction="column"
                  spacing={2}
                >
                  <Button
                    id="cancelBtn"
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      doCancelReservation();
                    }}
                    style={{
                      textTransform: "none",
                      fontWeight: "bolder",
                      letterSpacing: 2,
                      fontFamily: "Sofia Pro",
                      backgroundColor: "#f44336",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#d32f2f",
                      },
                    }}
                  >
                    Peruuta varaus
                  </Button>
                </Grid>
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item sm={12} md={6} xs={12} style={{ marginTop: 15, padding: 0 }}>
          <form onSubmit={formik.handleSubmit} noValidate>
            <Timeline
              style={{
                padding: 0,

                margin: 0,
                marginLeft: mobileViewPort ? 10 : 25,
              }}
            >
              <TimelineItem>
                <TimelineOppositeContent style={{ display: "none" }} />
                <TimelineSeparator>
                  <TimelineDot
                    style={{
                      backgroundColor:
                        expandedReservationStep === "contact-information"
                          ? theme.palette.primary.main
                          : Object.keys(formik.errors).length === 0
                          ? theme.palette.secondary.main
                          : theme.palette.primary.main,

                      transition: "150ms ease-in-out",
                    }}
                  >
                    {expandedReservationStep === "contact-information" ? (
                      <CreateIcon />
                    ) : reservationId ? (
                      <DoneIcon />
                    ) : Object.keys(formik.errors).length === 0 ? (
                      <DoneIcon />
                    ) : (
                      <CreateIcon />
                    )}
                  </TimelineDot>
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent style={{ padding: 0 }}>
                  <Accordion
                    expanded={expandedReservationStep === "contact-information"}
                    onChange={handleChange("contact-information")}
                    elevation={0}
                    sx={{ bgcolor: "transparent" }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="contact-informationbh-content"
                      id="contact-information-header"
                    >
                      <Typography
                        variant="h5"
                        style={{ padding: 0, margin: 0 }}
                      >
                        {translation.get(language, "Contact information")}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      style={{ padding: 0, paddingRight: "10px" }}
                    >
                      <Grid item container spacing={2}>
                        {!FEATURE_MIN_INPUT &&
                          !FEATURE_DISABLE_COMPANY_AS_CUSTOMER && (
                            <Grid container item>
                              <FormControlLabel
                                style={{
                                  color: theme.palette.primary.mainText,
                                }}
                                control={
                                  <Checkbox
                                    style={{
                                      color: theme.palette.primary.mainText,
                                    }}
                                    name="isCompany"
                                    color={
                                      disableFields ? undefined : "primary"
                                    }
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    disabled={disableFields}
                                  />
                                }
                                label={
                                  <Typography variant="body1">
                                    {translation.get(
                                      language,
                                      "Renter is a company"
                                    )}
                                  </Typography>
                                }
                              />
                            </Grid>
                          )}
                        {formik.values.isCompany && (
                          <>
                            <Grid item container spacing={2}>
                              {FEATURE_ENABLE_PAYMENT &&
                                (!submittedReservation?.paymentStatus ===
                                  "COMPLETED" ||
                                  !submittedReservation?.paymentStatus ===
                                    "CANCELLED") && (
                                  <>
                                    <Grid
                                      item
                                      xs={12}
                                      style={{
                                        paddingTop: 0,
                                        paddingBottom: 0,
                                      }}
                                    >
                                      <Typography
                                        variant="body1"
                                        style={{
                                          color: theme.palette.primary.mainText,
                                        }}
                                      >
                                        {translation.get(
                                          language,
                                          "Payment by invoice text"
                                        )}
                                      </Typography>
                                    </Grid>
                                  </>
                                )}
                              <Grid item xs={12} sm={7}>
                                <TextField
                                  InputProps={{
                                    style: {
                                      color: theme.palette.primary.mainText,
                                      backgroundColor:
                                        theme.palette.primary.dateRangeBg,
                                    },
                                  }}
                                  variant="outlined"
                                  fullWidth
                                  name="companyName"
                                  required
                                  label={translation.get(
                                    language,
                                    "Company name"
                                  )}
                                  value={formik.values.companyName}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  disabled={disableFields}
                                  error={
                                    formik.touched.companyName &&
                                    !!formik.errors.companyName
                                  }
                                  helperText={
                                    formik.touched.companyName &&
                                    formik.errors.companyName
                                  }
                                  size="small"
                                />
                              </Grid>
                              <Grid item xs={12} sm={5}>
                                <TextField
                                  InputProps={{
                                    style: {
                                      color: theme.palette.primary.mainText,
                                      backgroundColor:
                                        theme.palette.primary.dateRangeBg,
                                    },
                                  }}
                                  variant="outlined"
                                  fullWidth
                                  name="companyBusinessId"
                                  label={translation.get(
                                    language,
                                    "Business id"
                                  )}
                                  disabled={disableFields}
                                  value={formik.values.companyBusinessId}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={
                                    formik.touched.companyBusinessId &&
                                    !!formik.errors.companyBusinessId
                                  }
                                  helperText={
                                    formik.touched.companyBusinessId &&
                                    formik.errors.companyBusinessId
                                  }
                                  size="small"
                                />
                              </Grid>
                              {!FEATURE_ENABLE_PAYMENT && (
                                <>
                                  <Grid item xs={12} sm={7}>
                                    <TextField
                                      variant="outlined"
                                      fullWidth
                                      name="companyBillingAddress"
                                      label={translation.get(
                                        language,
                                        "Billing address"
                                      )}
                                      disabled={disableFields}
                                      value={
                                        formik.values.companyBillingAddress
                                      }
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                      error={
                                        formik.touched.companyBillingAddress &&
                                        !!formik.errors.companyBillingAddress
                                      }
                                      helperText={
                                        formik.touched.companyBillingAddress &&
                                        formik.errors.companyBillingAddress
                                      }
                                      size="small"
                                      InputProps={{
                                        style: {
                                          color: theme.palette.primary.mainText,
                                          backgroundColor:
                                            theme.palette.primary.dateRangeBg,
                                        },
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={5}>
                                    <TextField
                                      variant="outlined"
                                      fullWidth
                                      name="companyBillingRef"
                                      label={translation.get(
                                        language,
                                        "Cititation text"
                                      )}
                                      value={formik.values.companyBillingRef}
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                      disabled={disableFields}
                                      error={
                                        formik.touched.companyBillingRef &&
                                        !!formik.errors.companyBillingRef
                                      }
                                      helperText={
                                        formik.touched.companyBillingRef &&
                                        formik.errors.companyBillingRef
                                      }
                                      size="small"
                                      InputProps={{
                                        style: {
                                          color: theme.palette.primary.mainText,
                                          backgroundColor:
                                            theme.palette.primary.dateRangeBg,
                                        },
                                      }}
                                    />
                                  </Grid>
                                </>
                              )}
                            </Grid>
                          </>
                        )}
                        <Grid item xs={12} sm={12}>
                          <TextField
                            variant="outlined"
                            fullWidth
                            autoFocus
                            name="firstName"
                            required
                            label={translation.get(language, "Firstname")}
                            disabled={disableFields}
                            value={formik.values.firstName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.firstName &&
                              !!formik.errors.firstName
                            }
                            helperText={
                              formik.touched.firstName &&
                              formik.errors.firstName
                            }
                            autoComplete="firstName"
                            InputProps={{
                              style: {
                                color: theme.palette.primary.mainText,
                                backgroundColor:
                                  theme.palette.primary.dateRangeBg,
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                          <TextField
                            variant="outlined"
                            fullWidth
                            name="lastName"
                            required
                            label={translation.get(language, "Lastname")}
                            disabled={disableFields}
                            value={formik.values.lastName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.lastName &&
                              !!formik.errors.lastName
                            }
                            helperText={
                              formik.touched.lastName && formik.errors.lastName
                            }
                            autoComplete="lastName"
                            InputProps={{
                              style: {
                                color: theme.palette.primary.mainText,
                                backgroundColor:
                                  theme.palette.primary.dateRangeBg,
                              },
                            }}
                          />
                        </Grid>

                        <SocialSecurityNumberField
                          formik={formik}
                          authFailed={authFailed}
                          noMatchSSN={noMatchSSN}
                          disableFields={disableFields}
                          showSSN={showSSN}
                          handleClickShowSSN={handleClickShowSSN}
                          handleMouseDownPassword={handleMouseDownPassword}
                          language={language}
                          FEATURE_ASK_SSN={FEATURE_ASK_SSN}
                        />
                        {!FEATURE_MIN_INPUT && (
                          <>
                            <Grid item xs={12}>
                              <TextField
                                variant="outlined"
                                fullWidth
                                name="address"
                                required
                                label={translation.get(
                                  language,
                                  "Address label"
                                )}
                                disabled={disableFields}
                                value={formik.values.address}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={
                                  formik.touched.address &&
                                  !!formik.errors.address
                                }
                                helperText={
                                  formik.touched.address &&
                                  formik.errors.address
                                }
                                autoComplete="address-line1"
                                InputProps={{
                                  style: {
                                    color: theme.palette.primary.mainText,
                                    backgroundColor:
                                      theme.palette.primary.dateRangeBg,
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={8}>
                              <TextField
                                variant="outlined"
                                fullWidth
                                name="city"
                                required
                                label={translation.get(language, "City label")}
                                disabled={disableFields}
                                value={formik.values.city}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={
                                  formik.touched.city && !!formik.errors.city
                                }
                                helperText={
                                  formik.touched.city && formik.errors.city
                                }
                                autoComplete="address-level2"
                                InputProps={{
                                  style: {
                                    color: theme.palette.primary.mainText,
                                    backgroundColor:
                                      theme.palette.primary.dateRangeBg,
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item sm={4} xs={12}>
                              <TextField
                                variant="outlined"
                                fullWidth
                                name="postalCode"
                                required
                                label={translation.get(
                                  language,
                                  "Postal code label"
                                )}
                                disabled={disableFields}
                                value={formik.values.postalCode}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={
                                  formik.touched.postalCode &&
                                  !!formik.errors.postalCode
                                }
                                helperText={
                                  formik.touched.postalCode &&
                                  formik.errors.postalCode
                                }
                                autoComplete="postal-code"
                                InputProps={{
                                  style: {
                                    color: theme.palette.primary.mainText,
                                    backgroundColor:
                                      theme.palette.primary.dateRangeBg,
                                  },
                                }}
                              />
                            </Grid>
                          </>
                        )}
                        <Grid item xs={12}>
                          <TextField
                            variant="outlined"
                            fullWidth
                            label={translation.get(language, "Email label")}
                            name="email"
                            required
                            disabled={disableFields}
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.email && !!formik.errors.email
                            }
                            helperText={
                              formik.touched.email && formik.errors.email
                            }
                            autoComplete="email"
                            InputProps={{
                              style: {
                                color: theme.palette.primary.mainText,
                                backgroundColor:
                                  theme.palette.primary.dateRangeBg,
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            variant="outlined"
                            fullWidth
                            label={translation.get(
                              language,
                              "Email repeat label"
                            )}
                            name="emailRepeat"
                            required
                            disabled={disableFields}
                            value={formik.values.emailRepeat}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.emailRepeat &&
                              !!formik.errors.emailRepeat
                            }
                            helperText={
                              formik.touched.emailRepeat &&
                              formik.errors.emailRepeat
                            }
                            InputProps={{
                              style: {
                                color: theme.palette.primary.mainText,
                                backgroundColor:
                                  theme.palette.primary.dateRangeBg,
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <PhoneNumberField
                            required
                            name="phone"
                            translation={translation}
                            language={language}
                            fieldConfig={{
                              name: "phone",
                              label: translation.get(
                                language,
                                "Phone number label"
                              ),
                              countryCodeField: "phoneCountryCode",
                            }}
                            formik={formik}
                            fullWidth
                            InputProps={{
                              style: {
                                color: theme.palette.primary.mainText,
                                backgroundColor:
                                  theme.palette.primary.dateRangeBg,
                              },
                            }}
                          />
                        </Grid>

                        {!FEATURE_MIN_INPUT && (
                          <Grid item xs={12}>
                            <TextField
                              variant="outlined"
                              fullWidth
                              label={translation.get(
                                language,
                                "Message to dealer"
                              )}
                              name="externalNotes"
                              disabled={disableFields}
                              value={formik.values.externalNotes}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              multiline
                              InputProps={{
                                style: {
                                  color: theme.palette.primary.mainText,
                                  backgroundColor:
                                    theme.palette.primary.dateRangeBg,
                                },
                              }}
                            />
                          </Grid>
                        )}
                        {!FEATURE_MIN_INPUT &&
                          !FEATURE_DISABLE_ADDITIONAL_DRIVER && (
                            <Grid item container xs={12}>
                              <FormControlLabel
                                style={{
                                  color: theme.palette.primary.mainText,
                                }}
                                control={
                                  <Checkbox
                                    style={{
                                      color: theme.palette.primary.mainText,
                                    }}
                                    name="differentDriver"
                                    color="primary"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    disabled={disableFields}
                                    defaultChecked={
                                      submittedReservation?.differentDriver
                                    }
                                  />
                                }
                                label={FEATURE_DIFFERENT_DRIVER_LABEL}
                              />
                            </Grid>
                          )}
                        {formik.values.differentDriver && (
                          <>
                            <Grid item xs={12}>
                              <TextField
                                value={formik.values.driverName ?? ""}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={disableFields}
                                name="driverName"
                                label={translation.get(
                                  language,
                                  "Different drivers name"
                                )}
                                variant="outlined"
                                error={
                                  formik.touched.driverName &&
                                  !!formik.errors.driverName
                                }
                                helperText={
                                  formik.touched.driverName &&
                                  formik.errors.driverName
                                }
                                fullWidth
                                required
                                InputProps={{
                                  style: {
                                    color: theme.palette.primary.mainText,
                                    backgroundColor:
                                      theme.palette.primary.dateRangeBg,
                                  },
                                }}
                              />
                            </Grid>
                            {FEATURE_ASK_DRIVER_SSN && (
                              <Grid item xs={12}>
                                <TextField
                                  variant="outlined"
                                  fullWidth
                                  label={translation.get(
                                    language,
                                    "Different drivers ssn"
                                  )}
                                  name="driverSSN"
                                  required
                                  disabled={disableFields}
                                  value={formik.values.driverSSN}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  error={
                                    formik.touched.driverSSN &&
                                    !!formik.errors.driverSSN
                                  }
                                  helperText={
                                    formik.touched.driverSSN &&
                                    formik.errors.driverSSN
                                  }
                                  InputProps={{
                                    style: {
                                      color: theme.palette.primary.mainText,
                                      backgroundColor:
                                        theme.palette.primary.dateRangeBg,
                                    },
                                  }}
                                />
                              </Grid>
                            )}
                            <Grid item xs={12}>
                              <TextField
                                value={formik.values.driverAddress ?? ""}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={disableFields}
                                name="driverAddress"
                                label={translation.get(
                                  language,
                                  "Different drivers address"
                                )}
                                variant="outlined"
                                error={
                                  formik.touched.driverAddress &&
                                  !!formik.errors.driverAddress
                                }
                                helperText={
                                  formik.touched.driverAddress &&
                                  formik.errors.driverAddress
                                }
                                fullWidth
                                required
                                InputProps={{
                                  style: {
                                    color: theme.palette.primary.mainText,
                                    backgroundColor:
                                      theme.palette.primary.dateRangeBg,
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item sm={7} xs={12}>
                              <TextField
                                value={formik.values.driverCity ?? ""}
                                onChange={formik.handleChange}
                                disabled={disableFields}
                                onBlur={formik.handleBlur}
                                name="driverCity"
                                label={translation.get(
                                  language,
                                  "Different drivers city"
                                )}
                                variant="outlined"
                                error={
                                  formik.touched.driverCity &&
                                  !!formik.errors.driverName
                                }
                                helperText={
                                  formik.touched.driverCity &&
                                  formik.errors.driverName
                                }
                                fullWidth
                                required
                                InputProps={{
                                  style: {
                                    color: theme.palette.primary.mainText,
                                    backgroundColor:
                                      theme.palette.primary.dateRangeBg,
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item sm={5} xs={12}>
                              <TextField
                                value={formik.values.driverPostalCode ?? ""}
                                disabled={disableFields}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                name="driverPostalCode"
                                label={translation.get(
                                  language,
                                  "Different drivers postal number"
                                )}
                                variant="outlined"
                                error={
                                  formik.touched.driverPostalCode &&
                                  !!formik.errors.driverPostalCode
                                }
                                helperText={
                                  formik.touched.driverPostalCode &&
                                  formik.errors.driverPostalCode
                                }
                                fullWidth
                                required
                                InputProps={{
                                  style: {
                                    color: theme.palette.primary.mainText,
                                    backgroundColor:
                                      theme.palette.primary.dateRangeBg,
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <PhoneNumberField
                                required
                                name="driverPhone"
                                translation={translation}
                                language={language}
                                fieldConfig={{
                                  name: "driverPhone",
                                  label: translation.get(
                                    language,
                                    "Phone number label"
                                  ),
                                  countryCodeField: "driverPhoneCountryCode",
                                }}
                                formik={formik}
                                fullWidth
                                InputProps={{
                                  style: {
                                    color: theme.palette.primary.mainText,
                                    backgroundColor:
                                      theme.palette.primary.dateRangeBg,
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                value={formik.values.driverEmail ?? ""}
                                disabled={disableFields}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                name="driverEmail"
                                label={translation.get(
                                  language,
                                  "Different drivers email"
                                )}
                                variant="outlined"
                                error={
                                  formik.touched.driverEmail &&
                                  !!formik.errors.driverEmail
                                }
                                helperText={
                                  formik.touched.driverEmail &&
                                  formik.errors.driverEmail
                                }
                                fullWidth
                                required
                                InputProps={{
                                  style: {
                                    color: theme.palette.primary.mainText,
                                    backgroundColor:
                                      theme.palette.primary.dateRangeBg,
                                  },
                                }}
                              />
                            </Grid>
                          </>
                        )}
                        {formik.values.differentDriver &&
                          !FEATURE_DISABLE_ADDITIONAL_DRIVER && (
                            <Grid item container xs={12}>
                              <FormControlLabel
                                style={{
                                  color: theme.palette.primary.mainText,
                                }}
                                control={
                                  <Checkbox
                                    style={{
                                      color: theme.palette.primary.mainText,
                                    }}
                                    name="additionalDriver"
                                    color="primary"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    disabled={disableFields}
                                    defaultChecked={
                                      submittedReservation?.additionalDriver
                                    }
                                  />
                                }
                                label={
                                  <>
                                    {translation.get(
                                      language,
                                      "Additional driver"
                                    )}
                                  </>
                                }
                              />
                            </Grid>
                          )}

                        {formik.values.additionalDriver &&
                          formik.values.differentDriver && (
                            <>
                              <Grid item xs={12}>
                                <TextField
                                  disabled={disableFields}
                                  value={
                                    formik.values.additionalDriverName ?? ""
                                  }
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  name="additionalDriverName"
                                  label={translation.get(
                                    language,
                                    "Name label"
                                  )}
                                  variant="outlined"
                                  error={
                                    formik.touched.additionalDriverName &&
                                    !!formik.errors.additionalDriverName
                                  }
                                  helperText={
                                    formik.touched.additionalDriverName &&
                                    formik.errors.additionalDriverName
                                  }
                                  fullWidth
                                  required
                                  InputProps={{
                                    style: {
                                      color: theme.palette.primary.mainText,
                                      backgroundColor:
                                        theme.palette.primary.dateRangeBg,
                                    },
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <PhoneNumberField
                                  required
                                  name="additionalDriverPhone"
                                  translation={translation}
                                  language={language}
                                  fieldConfig={{
                                    name: "additionalDriverPhone",
                                    label: translation.get(
                                      language,
                                      "Phone number label"
                                    ),
                                    countryCodeField:
                                      "additionalDriverPhoneCountryCode",
                                  }}
                                  formik={formik}
                                  fullWidth
                                  InputProps={{
                                    style: {
                                      color: theme.palette.primary.mainText,
                                      backgroundColor:
                                        theme.palette.primary.dateRangeBg,
                                    },
                                  }}
                                />
                              </Grid>
                            </>
                          )}
                        {!FEATURE_DISABLE_CREDITCHECK && mobileViewPort && (
                          <Grid item xs={12}>
                            <ClickAwayListener onClickAway={handleTooltipClose}>
                              <FormControl
                                required
                                error={
                                  formik.touched.acceptCreditCheck &&
                                  !!formik.errors.acceptCreditCheck
                                }
                              >
                                <FormControlLabel
                                  style={{
                                    color: theme.palette.primary.mainText,
                                  }}
                                  control={
                                    <Checkbox
                                      defaultChecked={!!submittedReservation}
                                      style={{
                                        color: theme.palette.primary.mainText,
                                      }}
                                      name="acceptCreditCheck"
                                      color="primary"
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                      required
                                    />
                                  }
                                  label={
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "flex-start",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Typography
                                        style={{ fontFamily: "Roboto" }}
                                      >
                                        {FEATURE_CREDITCHECK_TITLE}
                                      </Typography>
                                      <Tooltip
                                        placement="top"
                                        PopperProps={{
                                          disablePortal: true,
                                        }}
                                        onClose={handleTooltipClose}
                                        open={openTooltip}
                                        disableFocusListener
                                        disableHoverListener
                                        disableTouchListener
                                        title={
                                          <div
                                            style={{
                                              padding: 5,
                                              borderRadius: 5,
                                              maxWidth: 300,
                                              display: "flex",
                                            }}
                                          >
                                            {FEATURE_CREDITCHECK_LABEL}
                                          </div>
                                        }
                                      >
                                        <InfoIcon
                                          onClick={handleTooltipOpen}
                                          style={{
                                            fontSize: 20,
                                            color: theme.palette.info.main,
                                            marginLeft: 7,
                                            paddingBottom: 15,
                                          }}
                                        />
                                      </Tooltip>
                                    </div>
                                  }
                                />
                                {formik.errors.acceptCreditCheck && (
                                  <FormHelperText>
                                    {formik.errors.acceptCreditCheck}
                                  </FormHelperText>
                                )}
                              </FormControl>
                            </ClickAwayListener>
                          </Grid>
                        )}

                        {!FEATURE_DISABLE_CREDITCHECK && !mobileViewPort && (
                          <Grid item xs={12}>
                            <Divider sx={{ marginBottom: "14px" }} />
                            <FormControl
                              required
                              error={!!formik.errors.acceptCreditCheck}
                            >
                              <FormControlLabel
                                style={{
                                  color: theme.palette.primary.mainText,
                                }}
                                control={
                                  <Checkbox
                                    defaultChecked={!!submittedReservation}
                                    style={{
                                      color: theme.palette.primary.mainText,
                                    }}
                                    name="acceptCreditCheck"
                                    color="primary"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    required
                                  />
                                }
                                label={
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "row",
                                      justifyContent: "flex-start",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Typography
                                      style={{ fontFamily: "Roboto" }}
                                    >
                                      {translation.get(
                                        language,
                                        "Credit check label"
                                      )}
                                    </Typography>
                                    <Tooltip
                                      placement="top"
                                      title={
                                        <div
                                          style={{
                                            padding: 5,
                                            borderRadius: 5,
                                            maxWidth: 300,
                                            display: "flex",
                                          }}
                                        >
                                          {FEATURE_CREDITCHECK_LABEL}
                                        </div>
                                      }
                                    >
                                      <InfoIcon
                                        style={{
                                          fontSize: 20,
                                          color: theme.palette.info.main,
                                          marginLeft: 7,
                                          paddingBottom: 15,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                }
                              />
                              {formik.errors.acceptCreditCheck &&
                                formik.touched.acceptCreditCheck && (
                                  <FormHelperText sx={{ marginBottom: "15px" }}>
                                    {formik.errors.acceptCreditCheck}
                                  </FormHelperText>
                                )}
                            </FormControl>
                          </Grid>
                        )}

                        {FEATURE_PRIVACY_POLICY && (
                          <Grid item xs={12}>
                            <FormControl
                              required
                              error={
                                formik.touched.acceptPrivacyPolicy &&
                                !!formik.errors.acceptPrivacyPolicy
                              }
                            >
                              <FormControlLabel
                                style={{
                                  color: theme.palette.primary.mainText,
                                }}
                                control={
                                  <Checkbox
                                    style={{
                                      color: theme.palette.primary.mainText,
                                    }}
                                    name="acceptPrivacyPolicy"
                                    color="primary"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    required
                                  />
                                }
                                label={
                                  <>
                                    {translation.get(language, "Accept text")}
                                    <Link
                                      href="https://orhi.fi/tietosuojaseloste/"
                                      target="_blank"
                                      rel="noopener"
                                    >
                                      {translation.get(
                                        language,
                                        "Privacy link"
                                      )}
                                    </Link>
                                  </>
                                }
                              />
                              {formik.touched.acceptPrivacyPolicy &&
                                formik.errors.acceptPrivacyPolicy && (
                                  <FormHelperText>
                                    {formik.errors.acceptPrivacyPolicy}
                                  </FormHelperText>
                                )}
                            </FormControl>
                          </Grid>
                        )}
                        {FEATURE_SHOW_PORT_INSTRUCTION && (
                          <Grid item xs={12}>
                            <FormControl
                              required
                              error={
                                formik.touched.acceptPortTerms &&
                                !!formik.errors.acceptPortTerms
                              }
                            >
                              <FormControlLabel
                                style={{
                                  color: theme.palette.primary.mainText,
                                }}
                                control={
                                  <Checkbox
                                    style={{
                                      color: theme.palette.primary.mainText,
                                    }}
                                    name="acceptPortTerms"
                                    color="primary"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    required
                                  />
                                }
                                label={
                                  <>
                                    {translation.get(language, "Accept text")}
                                    <Link
                                      href="https://www.rsteam.fi/vuokraamosta-lahto-paluu-ohjeistus/"
                                      target="_blank"
                                      rel="noopener"
                                    >
                                      {translation.get(
                                        language,
                                        "Port instructions link"
                                      )}
                                    </Link>
                                  </>
                                }
                              />
                              {formik.touched.acceptPortTerms &&
                                formik.errors.acceptPortTerms && (
                                  <FormHelperText>
                                    {formik.errors.acceptPortTerms}
                                  </FormHelperText>
                                )}
                            </FormControl>
                          </Grid>
                        )}

                        {!FEATURE_MUST_STRONG_AUTHENTICATE && (
                          <Grid item xs={12}>
                            <FormControl
                              required
                              error={
                                formik.touched.acceptTerms &&
                                !!formik.errors.acceptTerms &&
                                expandedReservationStep ===
                                  "contact-information"
                              }
                              style={{ marginBottom: 25 }}
                            >
                              <FormControlLabel
                                style={{
                                  color: theme.palette.primary.mainText,
                                }}
                                control={
                                  <Checkbox
                                    style={{
                                      color: theme.palette.primary.mainText,
                                    }}
                                    name="acceptTerms"
                                    color="primary"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    defaultChecked={
                                      submittedReservation?.authStatus ===
                                        "COMPLETED" || formik.values.acceptTerms
                                    }
                                    value={formik.values.acceptTerms}
                                    required
                                  />
                                }
                                label={
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "row",
                                      justifyContent: "flex-start",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Typography
                                      style={{
                                        fontFamily: "Roboto",
                                      }}
                                    >
                                      {translation.get(language, "Accept text")}
                                      {selectedCompany?.termsUrl ? (
                                        <Link
                                          href={selectedCompany?.termsUrl}
                                          target="_blank"
                                          rel="noopener"
                                        >
                                          {" "}
                                          {translation.get(
                                            language,
                                            "RENTAL TERMS"
                                          )}
                                        </Link>
                                      ) : (
                                        <>
                                          {" "}
                                          {translation.get(
                                            language,
                                            "RENTAL TERMS"
                                          )}
                                        </>
                                      )}
                                    </Typography>
                                  </div>
                                }
                              />

                              {formik.touched.acceptTerms &&
                                formik.errors.acceptTerms && (
                                  <FormHelperText>
                                    {formik.errors.acceptTerms}
                                  </FormHelperText>
                                )}
                            </FormControl>
                          </Grid>
                        )}

                        <Grid container justifyContent="center">
                          <Grid item xs={10} sx={{ marginBottom: "25px" }}>
                            <ButtonComponent
                              //  type={"submit"}
                              onClick={() => handleAdvanceStep()}
                              fullWidth={true}
                              disabled={submitting}
                              variant={"contained"}
                              buttonText={translation.get(
                                language,
                                "Continue reservation"
                              )}
                              fontSize={18}
                              color={"secondary"}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </TimelineContent>
              </TimelineItem>
              {FEATURE_MUST_STRONG_AUTHENTICATE &&
                !formik?.values?.bypassAuthentication && (
                  <TimelineItem>
                    <TimelineOppositeContent style={{ display: "none" }} />
                    <TimelineSeparator>
                      <TimelineDot
                        style={{
                          backgroundColor:
                            expandedReservationStep === "authentication"
                              ? theme.palette.primary.main
                              : submittedReservation?.authStatus === "COMPLETED"
                              ? theme.palette.secondary.main
                              : theme.palette.primary.main,
                          transition: "150ms ease-in-out",
                        }}
                      >
                        {expandedReservationStep === "authentication" ? (
                          <FingerprintIcon />
                        ) : submittedReservation?.authStatus === "COMPLETED" ? (
                          <DoneIcon />
                        ) : (
                          <FingerprintIcon />
                        )}
                      </TimelineDot>
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent style={{ padding: 0 }}>
                      <Accordion
                        expanded={expandedReservationStep === "authentication"}
                        onChange={handleChange("authentication")}
                        disabled={
                          reservationId ? false : disableAuthenticationStep
                        }
                        elevation={0}
                        style={{
                          backgroundColor: "transparent",
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="authenticationbh-content"
                          id="authenticationbh-header"
                        >
                          <Typography
                            variant="h5"
                            style={{ padding: 0, margin: 0 }}
                          >
                            {translation.get(language, "Authentication")}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails style={{ padding: 0 }}>
                          {FEATURE_MUST_STRONG_AUTHENTICATE &&
                            submittedReservation?.authStatus !==
                              "COMPLETED" && (
                              <Grid container direction="column">
                                {authFailed && (
                                  <Grid container direction={"column"}>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        color: theme.palette.warning.main,
                                        marginLeft: "25px",
                                        marginBottom: "15px",
                                      }}
                                    >
                                      {translation.get(
                                        language,
                                        "Authentication failed"
                                      )}
                                    </Typography>
                                  </Grid>
                                )}
                                <FormControl
                                  required
                                  error={
                                    formik.touched.acceptTerms &&
                                    !!formik.errors.acceptTerms &&
                                    expandedReservationStep === "authentication"
                                  }
                                  style={{ marginBottom: 25 }}
                                >
                                  <FormControlLabel
                                    style={{
                                      color: theme.palette.primary.mainText,
                                    }}
                                    control={
                                      <Checkbox
                                        style={{
                                          color: theme.palette.primary.mainText,
                                        }}
                                        name="acceptTerms"
                                        color="primary"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        defaultChecked={
                                          !!submittedReservation ||
                                          submittedReservation?.authStatus ===
                                            "COMPLETED"
                                        }
                                        required
                                      />
                                    }
                                    label={
                                      <div
                                        style={{
                                          display: "flex",
                                          flexDirection: "row",
                                          justifyContent: "flex-start",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Typography
                                          style={{
                                            fontFamily: "Roboto",
                                          }}
                                        >
                                          {translation.get(
                                            language,
                                            "Accept text"
                                          )}
                                          {selectedCompany?.termsUrl ? (
                                            <Link
                                              href={selectedCompany?.termsUrl}
                                              target="_blank"
                                              rel="noopener"
                                            >
                                              {translation.get(
                                                language,
                                                "RENTAL TERMS"
                                              )}
                                            </Link>
                                          ) : (
                                            <>
                                              {" "}
                                              {translation.get(
                                                language,
                                                "RENTAL TERMS"
                                              )}
                                            </>
                                          )}
                                          {FEATURE_MUST_STRONG_AUTHENTICATE && (
                                            <>
                                              {translation.get(
                                                language,
                                                "And text"
                                              )}
                                              <Link
                                                href={null}
                                                onClick={() =>
                                                  doPrintContract()
                                                }
                                              >
                                                {translation.get(
                                                  language,
                                                  "Rental agreement"
                                                )}
                                              </Link>
                                            </>
                                          )}
                                        </Typography>
                                        {FEATURE_MUST_STRONG_AUTHENTICATE && (
                                          <Tooltip
                                            placement="top"
                                            title={
                                              <div
                                                style={{
                                                  padding: 5,
                                                  borderRadius: 5,
                                                  maxWidth: 250,
                                                  display: "flex",
                                                }}
                                              >
                                                {translation.get(
                                                  language,
                                                  "Strong authentication tooltip"
                                                )}
                                              </div>
                                            }
                                          >
                                            <InfoIcon
                                              style={{
                                                fontSize: 20,
                                                color: theme.palette.info.main,
                                                marginLeft: 5,
                                                paddingBottom: 15,
                                              }}
                                            />
                                          </Tooltip>
                                        )}
                                      </div>
                                    }
                                  />

                                  {formik.touched.acceptTerms &&
                                    formik.errors.acceptTerms && (
                                      <FormHelperText>
                                        {formik.errors.acceptTerms}
                                      </FormHelperText>
                                    )}
                                </FormControl>

                                {submittedReservation?.authStatus ===
                                  "PENDING_MUST" && (
                                  <Grid container>
                                    <Typography
                                      variant="body1"
                                      sx={{ marginLeft: "17px" }}
                                    >
                                      {translation.get(
                                        language,
                                        "Authentication failed"
                                      )}
                                    </Typography>
                                  </Grid>
                                )}
                                <ButtonComponent
                                  type={"submit"}
                                  disabled={submitting}
                                  onClick={() => {
                                    if (submittedReservation) {
                                      formik.setFieldValue(
                                        "submitVariant",
                                        submitVariantEnum.authOnly
                                      );
                                    } else {
                                      formik.setFieldValue(
                                        "submitVariant",
                                        submitVariantEnum.upsertAndAuth
                                      );
                                    }
                                  }}
                                  fullWidth={true}
                                  variant={"contained"}
                                  buttonText={translation.get(
                                    language,
                                    "Proceed to authentication"
                                  )}
                                  fontSize={18}
                                  color={"secondary"}
                                />
                              </Grid>
                            )}

                          {submittedReservation?.authStatus === "COMPLETED" && (
                            <Grid container>
                              <Typography
                                variant="body1"
                                sx={{ marginLeft: "17px" }}
                              >
                                {translation.get(
                                  language,
                                  "Authentication completed"
                                )}
                              </Typography>
                            </Grid>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    </TimelineContent>
                  </TimelineItem>
                )}
              <TimelineItem>
                <TimelineOppositeContent style={{ display: "none" }} />
                <TimelineSeparator>
                  <TimelineDot
                    style={{
                      backgroundColor:
                        expandedReservationStep === "contact-information"
                          ? theme.palette.primary.main
                          : submittedReservation?.paymentStatus === "COMPLETED"
                          ? theme.palette.secondary.main
                          : theme.palette.primary.main,
                      transition: "150ms ease-in-out",
                    }}
                  >
                    {submittedReservation?.paymentStatus === "COMPLETED" ? (
                      <DoneIcon />
                    ) : (
                      <PaymentIcon />
                    )}
                  </TimelineDot>
                </TimelineSeparator>
                <TimelineContent style={{ padding: 0 }}>
                  <Accordion
                    expanded={expandedReservationStep === "payment-section"}
                    // expanded={true}
                    onChange={handleChange("payment-section")}
                    disabled={
                      submittedReservation?.authStatus === "COMPLETED"
                        ? false
                        : FEATURE_MUST_STRONG_AUTHENTICATE &&
                          !formik?.values?.bypassAuthentication
                        ? true
                        : Object.keys(formik.errors).length !== 0
                        ? true
                        : false
                    }
                    elevation={0}
                    style={{
                      backgroundColor: "transparent",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="payment-section-content"
                      id="payment-section-header"
                    >
                      <Typography
                        variant="h5"
                        style={{ padding: 0, margin: 0 }}
                      >
                        {translation.get(language, "Method of payment")}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails style={{ padding: 0 }}>
                      {FEATURE_PAYMENT_METHOD_SELECTOR && (
                        <Grid container sx={{ marginY: "55px" }} spacing={2}>
                          <Grid item xs={6}>
                            <Box
                              sx={{
                                border: "1px solid rgba(0,0,0,0.15)",
                                display: "flex",
                                flexDirection: "column",
                                borderRadius: "10px",
                                padding: "10px",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontFamily: "Sofia Pro",
                                  fontWeight: "bolder",
                                }}
                              >
                                Maksu noudettaessa
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: "Sofia Pro",
                                  fontWeight: "bolder",
                                  fontSize: "20px",
                                }}
                              >
                                {centsToLocalString(
                                  discountedTotalPrice * 1.25,
                                  true
                                )}
                              </Typography>
                              <Button
                                variant="outlined"
                                sx={{ marginTop: "10px" }}
                              >
                                Valitse
                              </Button>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sx={{ position: "relative" }}>
                            <Box
                              sx={{
                                position: "absolute",
                                bgcolor: "red",
                                top: -55,
                                width: "80%",
                                right: 10,
                                padding: "5px",
                                border: "2px solid rgba(0,0,0,0.10)",
                                borderRadius: "10px",
                                bgcolor: "rgba(0,0,0,0.05)",
                              }}
                            >
                              <Typography
                                align="center"
                                sx={{ fontSize: "14px", fontWeight: "bolder" }}
                              >
                                Maksamalla verkossa
                              </Typography>
                              <Typography
                                align="center"
                                sx={{ fontSize: "14px", fontWeight: "bolder" }}
                              >
                                säästät 25%
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                border: "1px solid rgba(0,0,0,0.15)",
                                display: "flex",
                                flexDirection: "column",
                                borderRadius: "10px",
                                padding: "10px",
                                boxShadow: theme.shadows[4],
                              }}
                            >
                              <Typography
                                sx={{
                                  fontFamily: "Sofia Pro",
                                  fontWeight: "bolder",
                                }}
                              >
                                Maksa verkossa
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: "Sofia Pro",
                                  fontWeight: "bolder",
                                  fontSize: "20px",
                                }}
                              >
                                {centsToLocalString(discountedTotalPrice, true)}
                              </Typography>
                              <Button
                                variant="contained"
                                color="success"
                                sx={{ marginTop: "10px" }}
                              >
                                Valitse
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      )}

                      {submittedReservation?.paymentStatus === "COMPLETED" && (
                        <Grid xs={12} item style={{ marginBottom: "10px" }}>
                          <Alert severity="success" variant="outlined">
                            {translation.get(language, "Payment completed")}
                          </Alert>
                        </Grid>
                      )}
                      {(submittedReservation?.paymentStatus === "CANCELLED" ||
                        (submittedReservation &&
                          !submittedReservation?.paymentStatus)) && (
                        <Grid xs={12} item style={{ marginBottom: "10px" }}>
                          <Alert severity="warning" variant="outlined">
                            {translation.get(language, "Payment on pick-up")}
                          </Alert>
                        </Grid>
                      )}
                      {showDepositInfo && (
                        <Grid item xs={12}>
                          <Alert severity="info" variant="outlined">
                            {translation.get(language, "Complete the payment")}
                          </Alert>
                        </Grid>
                      )}

                      {showPaymentSection && (
                        <Grid container>
                          {!FEATURE_HIDE_VOUCHER && (
                            <Voucher
                              language={language}
                              translation={translation}
                              theme={theme}
                              appliedVoucher={appliedVoucher}
                              setVoucher={setVoucher}
                              applyVoucher={applyVoucher}
                              validatingVoucher={validatingVoucher}
                              voucher={voucher}
                            />
                          )}
                          <Grid
                            item
                            xs={12}
                            justifyContent="space-between"
                            alignItems="center"
                            style={{ marginTop: "16px" }}
                          >
                            {(reservation.vehicle?.includedDistance ||
                              selectedServices.findIndex(
                                (serviceOffer) =>
                                  serviceOffer.category === "distance"
                              ) >= 0) && (
                              <>
                                <Grid item>
                                  <Typography variant="h5">
                                    {translation.get(
                                      language,
                                      "Included distance label"
                                    )}
                                  </Typography>
                                </Grid>
                                <Grid item style={{ textAlign: "right" }}>
                                  <Typography variant="h5" noWrap>
                                    {selectedServices.findIndex(
                                      (serviceOffer) =>
                                        serviceOffer.category === "distance"
                                    ) >= 0
                                      ? selectedServices.find(
                                          (serviceOffer) =>
                                            serviceOffer.category === "distance"
                                        ).description
                                      : reservation.vehicle?.includedDistance}
                                  </Typography>
                                </Grid>
                              </>
                            )}
                          </Grid>
                          {showPaymentSection &&
                            FEATURE_ENABLE_PAYMENT &&
                            discountedTotalPrice > 0 && (
                              <Grid item xs={12} style={{ marginBottom: 15 }}>
                                <RadioGroup
                                  style={{
                                    color: theme.palette.primary.mainText,
                                  }}
                                  aria-label={translation.get(
                                    language,
                                    "Payment now or upon pick-up"
                                  )}
                                  name="paymentMode"
                                  value={paymentMode}
                                  onChange={(event) =>
                                    setPaymentMode(event.target.value)
                                  }
                                >
                                  <FormControlLabel
                                    disabled={
                                      discountedTotalPrice > VISMA_PAY_MAX
                                    }
                                    value={PaymentModeEnum.now}
                                    control={
                                      <Radio
                                        style={{
                                          color: theme.palette.primary.main,
                                        }}
                                      />
                                    }
                                    label={`${translation.get(
                                      language,
                                      "Payment now label"
                                    )}  ${
                                      discountedTotalPrice > VISMA_PAY_MAX
                                        ? `(${translation.get(
                                            language,
                                            "Not available over"
                                          )} ${centsToLocalString(
                                            VISMA_PAY_MAX,
                                            true
                                          )} ${translation.get(
                                            language,
                                            "Reservations"
                                          )})`
                                        : "(Visma Pay)"
                                    }`}
                                  />
                                  {!FEATURE_PAYMENT_ONLINE_ONLY && (
                                    <FormControlLabel
                                      value={PaymentModeEnum.onPickup}
                                      control={
                                        <Radio
                                          style={{
                                            color: theme.palette.primary.main,
                                          }}
                                        />
                                      }
                                      label={FEATURE_PAYMENT_ON_PICKUP_LABEL}
                                    />
                                  )}
                                  {depositVisible && (
                                    <FormControlLabel
                                      value={PaymentModeEnum.now_deposit}
                                      control={
                                        <Radio
                                          style={{
                                            color: theme.palette.primary.main,
                                          }}
                                        />
                                      }
                                      disabled={!depositPossible}
                                      label={`${translation.get(
                                        language,
                                        "Pay text"
                                      )} ${centsToLocalString(
                                        selectedCompany.reservationDeposit
                                          .amount,
                                        true
                                      )} ${translation.get(
                                        language,
                                        "Booking deposit"
                                      )} 
                                        ${translation.get(
                                          language,
                                          "Remaining payment"
                                        )}
                                        ${
                                          selectedCompany.reservationDeposit
                                            .paymentBefore
                                        } ${translation.get(
                                        language,
                                        "Before the start of reservation"
                                      )}`}
                                    />
                                  )}
                                </RadioGroup>

                                <Grid container>
                                  <PaymentMethods
                                    formik={formik}
                                    paymentMode={paymentMode}
                                    paymentMethods={paymentMethods}
                                    setPaymentMethods={setPaymentMethods}
                                  />
                                </Grid>
                              </Grid>
                            )}

                          {showPaymentSection && (
                            <Grid container justifyContent="center">
                              <Grid item xs={10}>
                                <ButtonComponent
                                  onClick={() => {
                                    let variant = submitVariantEnum.upsertOnly;
                                    if (
                                      paymentMode === PaymentModeEnum.now ||
                                      paymentMode ===
                                        PaymentModeEnum.now_deposit
                                    ) {
                                      if (submittedReservation) {
                                        variant = submitVariantEnum.payOnly;
                                      } else {
                                        variant =
                                          submitVariantEnum.upsertAndPay;
                                      }
                                    }
                                    // Reservation already created, update and finalize.
                                    if (
                                      submittedReservation &&
                                      paymentMode === PaymentModeEnum.onPickup
                                    ) {
                                      variant =
                                        submitVariantEnum.updateForPickup;
                                    }
                                    formik.setFieldValue(
                                      "submitVariant",
                                      variant
                                    );
                                    formik.submitForm();
                                  }}
                                  fullWidth={true}
                                  disabled={submitting}
                                  variant={"contained"}
                                  buttonText={
                                    <>
                                      {FEATURE_ENABLE_PAYMENT &&
                                      paymentMode === PaymentModeEnum.now
                                        ? translation.get(
                                            language,
                                            "Go to payment"
                                          )
                                        : translation.get(
                                            language,
                                            "Make a reservation"
                                          )}
                                      {submitting && (
                                        <CircularProgress
                                          size={20}
                                          style={{ marginLeft: 5 }}
                                        />
                                      )}
                                    </>
                                  }
                                  fontSize={18}
                                  color={"secondary"}
                                />
                              </Grid>
                            </Grid>
                          )}
                        </Grid>
                      )}

                      {!submittedReservation?.disablePricesInPrintouts ? (
                        showDepositInfo ? (
                          <>
                            <Grid container item>
                              <PriceRow
                                label={translation.get(language, "Total text")}
                                price={
                                  submittedReservation?.paymentData.paymentTotal
                                }
                              />
                              <PriceRow
                                label={translation.get(language, "Deposit")}
                                price={
                                  -submittedReservation?.paymentData.deposit
                                }
                              />
                              <PriceRow
                                label={translation.get(
                                  language,
                                  "Amount to be paid"
                                )}
                                variant="h6"
                                price={discountedTotalPrice}
                                lastRow
                              />
                            </Grid>
                          </>
                        ) : (
                          <>
                            <PriceRow
                              label={translation.get(language, "Total text")}
                              price={discountedTotalPrice}
                              variant="h6"
                              lastRow
                            />
                          </>
                        )
                      ) : null}
                    </AccordionDetails>
                  </Accordion>
                </TimelineContent>
              </TimelineItem>
            </Timeline>
          </form>
        </Grid>
      </Grid>

      {!loading && (
        <Dialog
          open={openContractDialog}
          fullScreen={mobileViewPort}
          maxWidth={"md"}
        >
          <DialogContent sx={{ position: "relative", padding: 0 }}>
            <Box
              sx={{
                position: "sticky",
                top: 0,
                bgcolor: "white",
                marginBottom: "5px",
              }}
            >
              <Box display={"flex"} flex={1} justifyContent={"flex-end"}>
                <IconButton onClick={() => setOpenContractDialog(false)}>
                  <Close />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ overflow: "auto" }}>
              <ContractTemplatePreview
                values={formik?.values}
                cart={cart}
                selectedCompany={selectedCompany}
                ref={printRef}
                companyContractSettings={selectedCompany?.contractSettingsJSON}
              />
            </Box>
          </DialogContent>
        </Dialog>
      )}
      <Snackbar
        open={!!snackbarMessage}
        onClose={() => setSnackbarMessage(null)}
        autoHideDuration={10000}
        message={snackbarMessage}
      />
    </Container>
  );
}

function Voucher(props) {
  const {
    language,
    translation,
    theme,
    appliedVoucher,
    setVoucher,
    applyVoucher,
    validatingVoucher,
    voucher,
  } = props;

  return (
    <Grid item container xs={11} alignItems="center">
      <Grid item>
        <TextField
          variant="outlined"
          size="small"
          label={translation.get(language, "Voucher label")}
          name="voucher"
          value={voucher}
          onChange={(e) => setVoucher(e.target.value.toUpperCase())}
          disabled={!!appliedVoucher}
          InputProps={{
            style: {
              color: theme.palette.primary.mainText,
              backgroundColor: theme.palette.primary.dateRangeBg,
            },
          }}
        />
      </Grid>

      <Grid item sx={{ marginLeft: "10px" }}>
        {appliedVoucher ? (
          <Typography variant="h6">-{appliedVoucher.amount}%</Typography>
        ) : (
          <ButtonComponent
            onClick={applyVoucher}
            fullWidth={false}
            disabled={validatingVoucher}
            buttonText={translation.get(language, "Set label")}
            fontSize={16}
            color={"primary"}
          />
        )}
      </Grid>
    </Grid>
  );
}

function PrintContract({
  formik,
  selectedAdditionalServices,
  startTime,
  returnTime,
  appliedVoucher,
  reservation,
  cart,
  selectedCompany,
}) {
  const additionalServices = [];
  const reservationVehicles = [];
  for (let item of cart) {
    reservationVehicles.push({
      ...item.vehicle,
      price: reservation[0].offerPrice,
      tax: item.tax,
    });
  }
  for (let item of selectedAdditionalServices) {
    additionalServices.push({
      ...item,
      key: item.key || item.id,
      price: item.offerPrice,
    });
  }

  const parsedPhone = getPhone(formik.values, "phone");
  const parsedDriverPhone = getPhone(formik.values, "driverPhone");
  const parsedAdditionalDriverPhone = getPhone(
    formik.values,
    "additionalDriverPhone"
  );

  const name = getName(formik.values);

  const reservationData = {
    group: reservation[0].vehicle.group,
    reservationVehicles: reservationVehicles,
    companyId: reservation[0].vehicle.companyId,
    startTime: startTime,
    returnTime: returnTime,
    name: name,
    address: formik.values.address,
    postalCode: formik.values.postalCode,
    city: formik.values.city,
    phone: parsedPhone,
    email: formik.values.email,
    emailRepeat: formik.values.emailRepeat,
    externalNotes: formik.values.externalNotes,
    ssn: formik.values.ssn,
    additionalServices: additionalServices,
    voucherCode: appliedVoucher?.code,
    differentDriver: formik.values.differentDriver,
    driverName: formik.values.driverName,
    driverAddress: formik.values.driverAddress,
    driverPostalCode: formik.values.driverPostalCode,
    driverCity: formik.values.driverCity,
    driverPhone: parsedDriverPhone,
    driverEmail: formik.values.driverEmail,
    additionalDriver: formik.values.additionalDriver,
    additionalDriverName: formik.values.additionalDriverName,
    additionalDriverPhone: parsedAdditionalDriverPhone,
    enableRegistrationPlate:
      typeof selectedCompany?.enableRegistrationPlate === "boolean"
        ? selectedCompany?.enableRegistrationPlate
        : true,
    disablePricesInPrintouts: formik.values.disablePricesInPrintouts,
    isCompany: formik.values.isCompany,
    companyName: formik.values.companyName,
    companyBusinessId: formik.values.companyBusinessId,
    companyBillingRef: formik.values.companyBillingRef,
    companyBillingAddress: formik.values.companyBillingAddress,
  };

  const contractType =
    reservationVehicles[0]?.category?.typeId ?? "product-type-cars";
  //console.log("contractType", contractType);
  function _renderContract(contractType) {
    switch (contractType) {
      case 0:
        return;

      case "product-type-cars":
        return (
          <ContractTemplate
            reservation={reservationData}
            company={selectedCompany}
            additionalServices={selectedAdditionalServices}
            vehicles={reservationVehicles}
          />
        );
      case "product-type-vans":
        return (
          <ContractTemplate
            reservation={reservationData}
            company={selectedCompany}
            additionalServices={selectedAdditionalServices}
            vehicles={reservationVehicles}
          />
        );

      case "product-type-rvs":
        return (
          <ContractTemplate
            reservation={reservationData}
            company={selectedCompany}
            additionalServices={selectedAdditionalServices}
            vehicles={reservationVehicles}
          />
        );

      case "product-type-campers":
        return (
          <ContractTemplate
            reservation={reservationData}
            company={selectedCompany}
            additionalServices={selectedAdditionalServices}
            vehicles={reservationVehicles}
          />
        );

      case "product-type-trailers":
        return (
          <ContractTemplate
            reservation={reservationData}
            company={selectedCompany}
            additionalServices={selectedAdditionalServices}
            vehicles={reservationVehicles}
          />
        );

      case "product-type-snowmobiles":
        return (
          <ContractTemplateJetRental
            reservation={reservationData}
            company={selectedCompany}
            additionalServices={selectedAdditionalServices}
            vehicles={reservationVehicles}
          />
        );

      case "product-type-jetskis":
        return (
          <ContractTemplateJetRental
            reservation={reservationData}
            company={selectedCompany}
            additionalServices={selectedAdditionalServices}
            vehicles={reservationVehicles}
          />
        );

      case "product-type-bicycles":
        return (
          <ContractTemplateEbikeRental
            reservation={reservationData}
            company={selectedCompany}
            additionalServices={selectedAdditionalServices}
            vehicles={reservationVehicles}
          />
        );

      case "product-type-saunaboats":
        return (
          <ContractTemplate
            reservation={reservationData}
            company={selectedCompany}
            additionalServices={selectedAdditionalServices}
            vehicles={reservationVehicles}
          />
        );
      case "product-type-accommodations":
        return (
          <ContractTemplate
            reservation={reservationData}
            company={selectedCompany}
            additionalServices={selectedAdditionalServices}
            vehicles={reservationVehicles}
          />
        );

      default:
        return (
          <ContractTemplate
            reservation={reservationData}
            company={selectedCompany}
            additionalServices={selectedAdditionalServices}
            vehicles={reservationVehicles}
          />
        );
    }
  }

  return _renderContract(contractType);
}

function SocialSecurityNumberField({
  formik,
  authFailed,
  noMatchSSN,
  disableFields,
  showSSN,
  handleClickShowSSN,
  handleMouseDownPassword,
  FEATURE_ASK_SSN,
}) {
  const theme = useTheme();
  const { language, selectedCompany } = useContext(AppContext);
  const enableMustSignBypass =
    selectedCompany?.features?.["enableMustSignBypass"];

  return (
    <>
      {FEATURE_ASK_SSN && (
        <Grid item xs={12}>
          <Stack>
            <TextField
              type={showSSN ? "text" : "password"}
              autoFocus={authFailed || noMatchSSN}
              variant="outlined"
              fullWidth
              label={translation.get(language, "Social security number")}
              value={formik.values.ssn}
              autoComplete="off"
              name="ssn"
              required
              disabled={disableFields}
              //onChange={formik.handleChange}
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase();
                formik.handleChange(e);
              }}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.ssn && !!formik.errors.ssn) ||
                authFailed ||
                noMatchSSN
              }
              helperText={
                authFailed
                  ? translation.get(language, "Authentication failed")
                  : noMatchSSN
                  ? translation.get(language, "Auth no match SSN error")
                  : formik.touched.ssn && formik.errors.ssn
              }
              InputProps={{
                style: {
                  color: theme.palette.primary.mainText,
                  backgroundColor: theme.palette.primary.dateRangeBg,
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowSSN}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showSSN ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Grid>
      )}
      {enableMustSignBypass && (
        <Grid item xs={12}>
          <FormControlLabel
            label={translation.get(language, "No finnish ssn")}
            control={
              <Checkbox
                name="bypassAuthentication"
                color="primary"
                onChange={(e) => {
                  formik.setFieldValue(
                    "bypassAuthentication",
                    e.target.checked
                  );
                }}
                onBlur={formik.handleBlur}
                disabled={disableFields}
                defaultChecked={formik.values.bypassAuthentication}
              />
            }
          />
        </Grid>
      )}
    </>
  );
}
